/** Un nivel de ARHITECTURĂ, desenat: pereți în poché, goluri, camere, structură.
 *
 *  A treia vedere, lângă `PlanView` (parcajul) și `ApartmentView` (etajul de
 *  apartamente), și e vederea care lipsea. Celelalte două desenează ce
 *  GENEREAZĂ solverul — locuri, apartamente, nuclee, bare. Un nivel venit din
 *  import n-are niciunul: are pereții simplificați, ușile, ferestrele, camerele
 *  și structura desenată de arhitect, adică exact vocabularul unui plan. Fără
 *  vederea asta, un etaj importat cu 31 de compartimentări și 15 uși se
 *  deschidea ca pagină albă.
 *
 *  Convenția de desen e a planșei, nu a diagramei: pereții sunt corpuri pline
 *  (poché), golurile taie peretele și își arată tâmplăria, camerele poartă
 *  numele în cărămiziu și aria sub el. Structura desenată de arhitect —
 *  stâlpii și grinzile — stă dedesubt, mai stinsă: e context, nu subiect.
 *
 *  Spațiul e în METRI, ca peste tot în vizualizator: transformarea de deasupra
 *  e doar `translate` + `scale`, niciodată o răsturnare pe Y, deci un versor
 *  `(cos θ, sin θ)` din document e același versor pe ecran și un `rotate(θ)`
 *  SVG duce exact acolo.
 */
import { memo, useMemo, createContext, useContext } from "react";
import { cotaScrisa, liniileTreptelor, sageataUrca } from "./treptePlan";
import type { PanouFatada, StoreyPlan, StoreyShape } from "../canonical";
import { panouPesteSectiune } from "../canonical";
import { coteNivel, numaraCote, CoteGoale } from "./coteNivel";
import Cota, { COTA_EXT, COTA_INT } from "./Cota";
import { caleHasura, liniiHasura, HASURA_ROL, HASURA_IMPLICITA,
         MIN_PX_INTRE_LINII } from "./hasura";
import { culoareRezistenta, emblemaGolului, GRI_ALA, type Emblema } from "./rezistentaFoc";
import { pickAttrs, type PickAttrs, type Selection } from "../inspect";
import { conturZonei, Pardoseala, PRAG_HASURA } from "./arhMaterial";
import { Mobilier, PRAG_MOBILIER } from "./arhMobilier";
import { piesaPlan, piesaSingura, rangDesen } from "./arhMobilierPlan";
import { podesteleScarii, ScaraDinRampe } from "./ScaraDesen";

/** Spațiile al căror SIMBOL se desenează, fiindcă e semnul după care se
 *  recunosc: rampele scării, cabina liftului, țevile ghenei. Pe subsol vin ca
 *  `Space`, pe etaje ca `CorePart` (`nuclee`). Restul mobilierului nu se
 *  desenează — v. nota din desen. */
const SIMBOL_NUCLEU = new Set(["scara", "lift", "ghena"]);
import { gazdePosibile } from "../gazde";
import { esteCheie, useT, type Cheie } from "../../i18n";

// Aceeași familie ca `ApartmentView`: pereții sunt poché închis, numele
// camerelor cărămizii. Planșele de referință desenează planul în trei straturi
// deodată, iar potrivirea cu ele e mare parte din ce face un desen să arate a
// desen, nu a schemă.
const POCHE = "#4a4e52";          // peretele tăiat în secțiune
const POCHE_USOR = "#9aa0a6";     // parapetul / peretele care nu urcă
const HARTIE = "#f5f6f8";         // golul, adică lipsa de perete
const NUME = "#086a7a";           // numele camerei
// Reperele proiectate din alt plan: gri-albastrui, ca să nu se confunde nici cu
// pereții (negru), nici cu structura (poșă), nici cu selecția (cărămiziu).
const REPER = "#6b7a8f";
const REPER_COD = 0.7;            // înălțimea codului de corp pe reper [m]
// Traseele de evacuare din justificarea unei scări: roșu drumul care depășea
// limita, verde cel care o respectă. Verdele NU e cel al selecției (cărămiziu),
// ca să nu se citească drept «obiectul ales».
const EVAC_LUNG = "#c0392b";
const EVAC_SCURT = "#4f8207";
const INK = "#33383d";
const STRUCT = "#7d8a93";         // stâlpii și grinzile arhitectului
/** Placa nivelului. Trebuie să fie clar MAI ÎNCHISĂ decât o cameră: la
 *  încadrarea pe tot ansamblul (6 px/m) nu se mai văd nici hașura, nici
 *  mobilierul, nici etichetele — singurul lucru care mai spune unde sunt
 *  apartamentele e diferența de ton dintre placă și pardoseală. Dusă prea
 *  aproape de tonul camerei, banda de apartamente dispare cu totul și rămân
 *  șase dreptunghiuri goale; s-a și întâmplat. */
const PLACA_FILL = "#dadada";
// Peretele din vederea randată: aproape alb, cu o muchie abia vizibilă. Nu e o
// alegere de gust — e ce fac AMÂNDOUĂ planșele de referință, iar diferența
// dintre un plan care arată a CAD și unul care arată a randare stă mai mult în
// asta decât în orice altceva: acolo peretele nu e desenat, e luminat.
const PERETE_ALB = "#fafafa";
const PERETE_ALB_USOR = "#ebebeb";
/** Rolurile care NU țin nimic: se desenează mai deschis, ca greutatea din desen
 *  să spună aceeași poveste ca structura. Antifocul nu e aici — el e greu ȘI
 *  are muchia lui (`var(--fire)`, aceeași cu a planșei de parcaj, ca un perete
 *  antifoc să arate la fel oriunde apare). */
const USOR = new Set(["parapet", "partition"]);
/** Betonul armat al unui adăpost ALA. Pe stratul ISU e gri (`GRI_ALA`); cu
 *  stratul stins e un perete ca oricare altul — o culoare care nu spune o clasă
 *  nu spune nimic (Oana, 20.10.2026; până atunci era roșu de planșă de adăpost).
 *  Griul e al pereților adăpostului, nu al oricărui `ala_perete`: puțul
 *  săriturii de lup (`rol: "saritura"`) e beton obișnuit în afara anvelopei și,
 *  colorat, trăgea privirea de pe adăpost pe o groapă de 1 m. */
const eBetonAdapost = (w: StoreyShape) => w.kind === "ala_perete" && w.ala?.rol !== "saritura";
/** Scara de pisică e de oțel: gri, ca să nu iasă din plan mai mult decât puțul. */
const OTEL = "#8b9096";

// PERETELE TĂIAT PE PLANȘA TEHNICĂ: ÎNNEGRIT, nu hașurat.
// ---------------------------------------------------------------------------
//
// STAS 104-80 (azi SR ISO 128-50): «Secțiunile obiectelor a căror lățime pe
// desen este mai mică de 2 mm se evidențiază prin ÎNNEGRIRE COMPLETĂ.» La
// 1:100 — scara la care se desenează un etaj de mărimea ăstuia — 2 mm de
// planșă înseamnă 20 cm de zid, iar pereții documentului au între 10 și 30 cm.
// Deci regula îi prinde pe aproape toți: se înnegresc, nu se hașurează. Așa
// arată și planșa de referință românească (A07), unde pereții sunt pline, iar
// hașura apare doar ca marcaj de rezistență la foc.
//
// Planșa americană din care s-a pornit hașurează fiindcă e la 1/4"=1'-0",
// adică ~1:48: acolo același zid are 6 mm și hașura are unde să încapă.
//
// DE CE NU HAȘURĂM TOTUȘI, pe roluri: `Wall.kind` e ROLUL (structural, party,
// partition, facade, fire, parapet), nu materialul — documentul nu spune din ce
// e făcut niciun perete. O hașură „de beton" pe rolul portant ar fi o afirmație
// despre material pe care n-o susține nimic. Deosebirea între roluri se face
// unde e onestă: din bifele de strat (Structurali / De fațadă / Compartimentări
// / Antifoc), care izolează exact ce vrei să vezi. Când `Wall` va purta
// `material`, hașura devine posibilă ȘI adevărată.
//
// PARAPETUL face excepție: nu ajunge la planul de secțiune (~1,15 m), deci nu e
// un element TĂIAT, ci unul VĂZUT — alb, cu contur subțire.

/** Mobilierul, ca desen de linie: toate umplerile stinse, conturul cerneală.
 *  V. nota din `arhMobilier` despre de ce merge prin variabile CSS. */
/** Înălțimea de nivel pe care o presupune desenul scării (contratreptele),
 *  când documentul n-o spune pe piesă: cea a etajului curent. */
const INALTIME_NIVEL_M = 3.0;
/** Înălțimea unui nivel de subsol, între plăci (`architecture/inaltimi.py::H_NIVEL`):
 *  cât urcă o scară de evacuare a parcajului pe un nivel. */
const INALTIME_SUBSOL_M = 2.5;

const SCHITA_MOBILIER = {
  // ALB, nu transparent. Piesele din desenul de referință sunt PLINE cu hârtie
  // și conturate: o canapea acoperă ce e sub ea, cum acoperă și în realitate.
  // Lăsate transparente, hașura peretelui și liniile de dedesubt treceau prin
  // ele, iar mobilierul arăta ca o sârmă peste zid, nu ca un obiect așezat pe
  // pardoseală. Hârtia vine din variabilă, deci se întoarce odată cu planșa pe
  // tuș de noapte.
  "--mob-linie": "var(--tus, #111111)",
  "--mob-corp": "var(--hartie, #ffffff)",
  "--mob-lemn": "var(--hartie, #ffffff)",
  "--mob-moale": "var(--hartie, #ffffff)",
  "--mob-covor": "var(--hartie, #ffffff)",
  "--mob-apa": "var(--hartie, #ffffff)",
  "--mob-verde": "var(--hartie, #ffffff)",
  "--mob-umbra": "none",
} as React.CSSProperties;

/** Peretele care NU e tăiat de planul de secțiune. */
const NESECTIONAT = new Set(["parapet"]);

/** Grosimile, pe familia STAS b / b/2 / b/4: conturul secționat cel mai gros,
 *  muchia văzută la jumătate, hașura și cotele la un sfert. */
export const TUS = { taiat: 1.5, vazut: 0.75, subtire: 0.4 };
/** Sub lățimea asta PE ECRAN zidul tăiat se înnegrește (regula STAS de mai
 *  sus, dusă la zoom): e lățimea la care cele două contururi `taiat` — fixe,
 *  la orice scară — nu mai lasă nici un pixel de fond între ele. Sub ea fondul
 *  și hașura nu se mai văd oricum, iar conturul doar îngroașă zidul cu 1,5 px
 *  peste cât e de lat: un zid de 0,30 m la 10 px/m iese o bară neagră de
 *  4,5 px, de trei-patru ori mai grea decât orice altă linie a planșei.
 *  Înnegrit, zidul e exact cât e de lat. */
export const ZID_INNEGRIT_PX = 2 * TUS.taiat + 1;
/** Sub lățimea asta a GOLULUI pe ecran, săritura de lup se desenează DISCRET:
 *  hârtie cu contur `vazut`, ca un element văzut, nu tăiat. Puțul are gol de
 *  1,00 m între pereți de 0,40 m; înnegrit, inelul se închide la zoom mic
 *  într-un pătrat negru de ~2 m — pe planșa subsolului, zeci de pete mai grele
 *  decât nucleele, care trăgeau privirea de pe parcaj pe o groapă de 1 m
 *  (Oana, 22.10.2026). Peste prag golul se vede și puțul redevine zid tăiat. */
export const PUT_DISCRET_PX = 16;
const GOL_PUT_M = 1.0;
const ePutDiscret = (w: StoreyShape, pxPerM: number) =>
  w.ala?.rol === "saritura" && GOL_PUT_M * pxPerM < PUT_DISCRET_PX;
/** Grosimea zidului, în metri: cea declarată, altfel dedusă din inel. 2·A/P e
 *  exact `t` pe un dreptunghi lung și rămâne lângă el pe un inel în L sau
 *  închis (golurile intră și în arie, și în perimetru). */
function grosimeZid(w: StoreyShape): number {
  if (w.thickness_m && w.thickness_m > 0) return w.thickness_m;
  let arie = 0, per = 0;
  [w.poly, ...(w.holes ?? [])].forEach((inel, k) => {
    let s = 0;
    for (let i = 0; i < inel.length; i++) {
      const [x0, y0] = inel[i], [x1, y1] = inel[(i + 1) % inel.length];
      s += x0 * y1 - x1 * y0;
      per += Math.hypot(x1 - x0, y1 - y0);
    }
    // golurile se SCAD din arie: inelul exterior le cuprinde
    arie += (k === 0 ? 1 : -1) * Math.abs(s) / 2;
  });
  return per > 0 ? (2 * arie) / per : 0;
}
/** O SINGURĂ GAMĂ DE CULOARE pentru apartamente: verde-albastru petrol, de la
 *  închis la deschis, nu șase nuanțe din tot cercul (bleu, verde, roz, galben)
 *  care făceau planșa pestriță. Tonurile sunt saturate, iar transparența mare:
 *  materialul pardoselii se citește PRIN ele, iar spălătura spune «același
 *  apartament», nu înlocuiește ce e dedesubt.
 *
 *  Vecinii se deosebesc prin LUMINOZITATE, nu prin nuanță, așa că ordinea
 *  alternează închis / deschis: două poziții alăturate din listă — doi vecini pe
 *  o bandă, sau M1 și M2 — nu cad niciodată pe trepte apropiate. */
// Saturate la bază și nimic închis: amestecat cu albul camerei, un albastru
// închis iese cenușiu, nu albastru. Vecinii se deosebesc prin luminozitate ȘI
// prin alunecarea nuanței în aceeași familie — de la cyan-albastru la azur.
const GAMA_APARTAMENTE = ["#0f86d1", "#62c3f0", "#14a3c0", "#2b7fd6",
                          "#8fd3f5", "#1aa1d6", "#4fb3e8", "#3a6fd8"];
const TRANSPARENTA_APARTAMENT = "50";       // ~31 % opacitate
/** Spălătura apartamentului — v. locul unde se desenează. Șase tonuri ajung ca
 *  doi vecini să nu se atingă niciodată în aceeași culoare pe o bandă. */
const SPALATURA = GAMA_APARTAMENTE.slice(0, 6).map((c) => c + TRANSPARENTA_APARTAMENT);

/** Spălătura pe TRONSON: aceeași culoare pentru tot ce a ieșit din același
 *  modul, în toate blocurile și pe toate nivelurile.
 *
 *  Asta e verificarea pentru care există: catalogul promite că M1 iese la fel
 *  oriunde apare, iar o planșă în care modulul are o culoare a lui o arată
 *  dintr-o privire — un M1 care nu seamănă cu celelalte M1 sare în ochi fără să
 *  citești nicio etichetă.
 *
 *  Culoarea se ia din NUMĂRUL modulului, nu din ordinea în care apar pe nivel:
 *  altfel M1 ar fi închis pe un etaj și deschis pe altul, după cine mai e acolo, iar
 *  comparația dintre două niveluri ar fi imposibilă. Opt tonuri, cât are
 *  catalogul module, ca niciunul să nu împrumute culoarea altuia. */
const SPALATURA_TRONSON = GAMA_APARTAMENTE.map((c) => c + TRANSPARENTA_APARTAMENT);

/** Culoarea unui tronson, din codul lui. `M3` -> al treilea ton.
 *
 *  Un cod fără cifră — dacă vreodată catalogul capătă unul — primește un ton
 *  dintr-un hash al șirului: stabil, chiar dacă nu mai e ordonat. */
export function culoareaTronsonului(cod: string): string {
  const n = /(\d+)\s*$/.exec(cod);
  if (n) return SPALATURA_TRONSON[(Number(n[1]) - 1) % SPALATURA_TRONSON.length];
  let h = 0;
  for (let i = 0; i < cod.length; i++) h = (h * 31 + cod.charCodeAt(i)) >>> 0;
  return SPALATURA_TRONSON[h % SPALATURA_TRONSON.length];
}
/** Spălătura unui apartament: a tronsonului din care a ieșit, când se știe,
 *  altfel a rangului lui pe nivel. Aceeași regulă pe desenul nivelului și pe
 *  masa lui (v. `EditorNivel`), ca un apartament să nu-și schimbe culoarea
 *  când treci de pe una pe alta.
 *
 *  PE COPIA UNUI MODUL IZOLAT toate apartamentele vin din același modul, deci
 *  tronsonul le-ar da o singură culoare la toate; acolo culoarea vine din
 *  NUMĂRUL apartamentului în modul (`apartament`) — același apartament, aceeași
 *  culoare pe toate etajele și în toate copiile, ca M1 pe bare. */
export function culoareaApartamentului(tronson: string | undefined, i: number,
                                       apartament?: number): string {
  if (apartament != null) return SPALATURA_TRONSON[apartament % SPALATURA_TRONSON.length];
  return tronson ? culoareaTronsonului(tronson) : SPALATURA[i % SPALATURA.length];
}
const PERETE_MUCHIE = "#d9d9d9";
/** Cifra cheii de legendă în vederea randată: brun cald, subțire, nu cărămiziul
 *  apăsat al planului tehnic. Pe Cheshme cifrele sunt aproape șoptite — sunt o
 *  cheie, nu un titlu. */
const CIFRA = "#516a6c";

// ---------------------------------------------------------------------------
// CELE DOUĂ CONVENȚII ALE ACELUIAȘI DESEN
// ---------------------------------------------------------------------------
//
// «Plan» și «2D» sunt ACELAȘI desen, din aceleași obiecte, cu aceleași straturi
// — se deosebesc doar prin cum arată linia și tonul:
//
//   `tehnic` — planșa de execuție, alb-negru: peretele e poché ÎNCHIS, camera e
//              hârtie, materialul și spălătura de apartament nu se desenează,
//              umbra nu există. Asta e ce se predă și ce se verifică, și e
//              convenția planșei de referință (A07).
//   `randat` — planul de prezentare: pereți albi citiți prin umbră, pardoseli
//              pe funcțiune, spălătura apartamentului. Ce aveam până acum.
//
// A MAI FOST un comutator «Randat»/«Tehnic», și a fost scos — pe drept, atunci:
// tot ce deosebea modurile ținea de CAMERE, iar un subsol de parcaj n-are
// camere, deci pe jumătate din niveluri butonul nu făcea nimic. De atunci s-a
// schimbat ce e pe desen: sunt pereți (913 pe un etaj real), materiale,
// mobilier și spălătură de apartament, deci cele două convenții chiar arată
// diferit — și una din ele e cea în care se lucrează în practică.
//
// Ce NU se schimbă între ele: obiectele, straturile, cotele, selecția. O
// convenție e o foaie de stil, nu un al doilea desen — altfel ar fi două locuri
// care decid ce se vede, iar cele două ar diverge.

export type Conventie = "tehnic" | "tehnic_noapte" | "randat";

export interface Paleta {
  perete: string; pereteUsor: string; muchie: string;
  placa: string; struct: string; nume: string; cifra: string;
  /** Materialul pardoselii și spălătura apartamentului se desenează? */
  culoare: boolean;
  /** Tonul arcului de deschidere: din familia cernelii, dar mai slab — un arc
   *  e o mișcare posibilă, nu o muchie construită. */
  arc: string;
  /** Cerneala și hârtia planșei de linie. Pe planșa de zi sunt negru pe alb; pe
   *  cea de noapte se schimbă între ele — aceeași planșă, alt tuș, exact ca
   *  ecranul unui CAD. Toate liniile desenului tehnic le citesc de aici, ca
   *  întoarcerea să fie o singură decizie, nu treizeci. */
  tus: string; hartie: string;
  /** Fondul zidului tăiat pe planșa de linie: gri de tuș, ca pe masa
   *  tronsonului (`tronson/Desen.ZID_FOND`), nu hârtie — v. nota de acolo. */
  zid: string;
}

export const PALETA: Readonly<Record<Conventie, Paleta>> = {
  randat: {
    perete: PERETE_ALB, pereteUsor: PERETE_ALB_USOR, muchie: PERETE_MUCHIE,
    placa: PLACA_FILL, struct: STRUCT, nume: NUME, cifra: CIFRA,
    // Planul randat nu e desen de linie, deci nu are „tuș" și „hârtie" — dar
    // câmpurile există pe tip, iar valorile astea sunt cele pe care le-ar avea
    // dacă cineva le-ar citi din greșeală de-acolo.
    culoare: true, tus: INK, hartie: HARTIE, arc: POCHE_USOR, zid: PERETE_ALB,
  },
  tehnic: {
    perete: POCHE, pereteUsor: POCHE_USOR, muchie: INK,
    placa: "#ffffff", struct: POCHE, nume: INK, cifra: INK,
    culoare: false, tus: "#111111", hartie: "#ffffff", arc: "#8b9096",
    // = `tronson/Desen.ZID_FOND`; scris aici, fiindcă Desen importă din fișierul ăsta.
    zid: "#ececec",
  },
  // PLANȘA DE NOAPTE: aceeași planșă, cu tușul și hârtia schimbate între ele.
  // Nu e un „dark mode" al interfeței — e desenul însuși, alb pe negru, cum se
  // lucrează în CAD. Cerneala nu e alb pur și hârtia nu e negru pur: pe ecran,
  // contrastul maxim vibrează pe liniile subțiri, iar planșa asta e făcută
  // aproape numai din linii subțiri.
  //
  // Hârtia e CERNEALA LOGOULUI (26.09.2026), aceeași familie cu antetul, nu un
  // negru neutru: noaptea planșei și bara de deasupra ei sunt un singur fond.
  tehnic_noapte: {
    perete: "#0e1424", pereteUsor: "#1a2236", muchie: "#e6eaef",
    placa: "#0e1424", struct: "#e6eaef", nume: "#e6eaef", cifra: "#e6eaef",
    culoare: false, tus: "#e6eaef", hartie: "#0e1424", arc: "#7f8a9c",
    zid: "#232c44",
  },
};

/** Convenția, pe context: `Gol` și celelalte semne o citesc fără s-o care
 *  fiecare părinte prin toate nivelurile de props. */
const ConventieCtx = createContext<Paleta>(PALETA.randat);
export const usePaleta = () => useContext(ConventieCtx);

const puncte = (poly: number[][]) => poly.map((p) => `${p[0]},${p[1]}`).join(" ");

/** Inelul și golurile lui, ca `d` de `<path>` — `fill-rule: evenodd` scoate
 *  găurile. Un `<polygon>` nu poate avea gaură, iar un inel desenat fără ea e o
 *  pată: aticul ultimului etaj acoperea toată placa. */
const caleCuGoluri = (poly: number[][], holes?: number[][][]) =>
  [poly, ...(holes ?? [])]
    .filter((r) => r.length >= 3)
    .map((r) => `M${r.map((p) => `${p[0]},${p[1]}`).join("L")}Z`)
    .join("");

/** Centrul inelului, pentru eticheta camerei. Centroidul vârfurilor, nu al
 *  ariei: nu cere aria orientată. Pe o cameră în L sau în U, sau cu o gaură,
 *  poate cădea în afara ei — un adăpost ALA ocolește nucleul scării, iar
 *  numele lui se scria peste scară (26.09.2026) —, și atunci eticheta ia
 *  mijlocul celei mai late bucăți din cameră a orizontalei prin el. */
/** Unde și cât de mare se scrie un nume pe o placă: pe direcția celei mai lungi
 *  laturi (întoarsă ca textul să nu stea cu capul în jos), în centrul plăcii,
 *  cu litera cât încape textul pe lungime și pe lățime — pe un rând sau, când
 *  așa iese mai mare, pe două (cuvintele împărțite în două jumătăți). `null`
 *  când placa e prea mică pentru o literă de 12 cm — atunci rămâne hașura. */
export function etichetaPePlaca(poly: number[][], text: string):
    { x: number; y: number; unghi: number; marime: number; randuri: string[] } | null {
  if (poly.length < 3) return null;
  let best = 0, ux = 1, uy = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (L > best) { best = L; ux = (b[0] - a[0]) / L; uy = (b[1] - a[1]) / L; }
  }
  if (ux < 0) { ux = -ux; uy = -uy; }
  // întinderea plăcii de-a lungul și de-a curmezișul direcției alese
  let lo = Infinity, hi = -Infinity, vlo = Infinity, vhi = -Infinity;
  for (const [x, y] of poly) {
    const u = x * ux + y * uy, v = -x * uy + y * ux;
    lo = Math.min(lo, u); hi = Math.max(hi, u); vlo = Math.min(vlo, v); vhi = Math.max(vhi, v);
  }
  const lungime = hi - lo, latime = vhi - vlo;
  // ~0,58 din înălțimea literei pe caracter, cu 15 % margine; un rând ia
  // ~1,25 din înălțimea literei
  const MAX = 0.45;
  const pe = (randuri: string[]) => Math.min(MAX,
    (0.85 * lungime) / (0.58 * Math.max(1, ...randuri.map((r) => r.length))),
    (0.7 * latime) / (1.25 * randuri.length));
  const cuvinte = text.split(/\s+/).filter(Boolean);
  const jum = Math.ceil(cuvinte.length / 2);
  const variante = [[text], ...(cuvinte.length > 1
    ? [[cuvinte.slice(0, jum).join(" "), cuvinte.slice(jum).join(" ")]] : [])];
  const randuri = variante.reduce((a, b) => (pe(b) > pe(a) ? b : a));
  const marime = pe(randuri);
  if (!(marime >= 0.12)) return null;
  const [x, y] = centru(poly);
  return { x, y, unghi: (Math.atan2(uy, ux) * 180) / Math.PI, marime, randuri };
}

export function centru(poly: number[][], holes?: number[][][]): [number, number] {
  let x = 0, y = 0;
  for (const p of poly) { x += p[0]; y += p[1]; }
  x /= poly.length; y /= poly.length;
  const xs: number[] = [];
  for (const inel of [poly, ...(holes ?? [])]) {
    for (let i = 0; i < inel.length; i++) {
      const [x0, y0] = inel[i], [x1, y1] = inel[(i + 1) % inel.length];
      if ((y0 > y) !== (y1 > y)) xs.push(x0 + ((y - y0) * (x1 - x0)) / (y1 - y0));
    }
  }
  xs.sort((a, b) => a - b);
  let lat: [number, number] | null = null;
  for (let i = 0; i + 1 < xs.length; i += 2) {
    if (x >= xs[i] && x <= xs[i + 1]) return [x, y];
    if (!lat || xs[i + 1] - xs[i] > lat[1] - lat[0]) lat = [xs[i], xs[i + 1]];
  }
  return lat ? [(lat[0] + lat[1]) / 2, y] : [x, y];
}

/** Cât de mare e obiectul, în metri, pe latura lui mică — cu asta se decide
 *  dacă eticheta încape. Un „Hol" scris peste o cameră de 0,9 m lățime nu
 *  informează, acoperă. */
/** Adăpostul ALA și ieșirile lui (săritura de lup, culoarul): goluri între
 *  pereți, desenate fără pardoseală — v. fondul din `Pardoseala`. */
// …și holul SAS al căilor de evacuare, din același motiv: peste el stau
// pereții lui și ușile EI 60-C, iar fondul alb le acoperea (Oana, 19.10.2026)
const eGolAla = (r: StoreyShape): boolean =>
  r.kind === "ala" || r.kind === "ala_iesire" || !!r.evacuare;
// Coridorul căii de evacuare (`coridor_evacuare`, pasul 5 din `evacuare/pas.py`):
// fâșia de 0,90 m fără pereți de la alee la ușa SAS. Verde, ca traseele din
// stratul «Căi de evacuare», cu transparență: dedesubt rămân locurile și stâlpii.
const CORIDOR_EVACUARE = "rgba(0, 128, 63, 0.28)";
const eCoridorEvacuare = (r: StoreyShape): boolean => r.kind === "coridor_evacuare";

function latura(poly: number[][]): number {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of poly) {
    x0 = Math.min(x0, x); y0 = Math.min(y0, y);
    x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  }
  return Math.min(x1 - x0, y1 - y0);
}

/** Unde se scrie numele fiecărui spațiu, ca două etichete să nu se calce.
 *
 *  Pe planul importat suprafețele se SUPRAPUN prin construcție: arhitectul a
 *  scris și „Living + bucătărie", și „AP_2 camere" peste aceeași parte de etaj,
 *  iar importul le păstrează pe amândouă. Scrise amândouă în centru, ieșea un
 *  ghem ilizibil. Se așază de la mic la mare — camera mică n-are unde să se
 *  ducă, zona are — și ce nu încape nicăieri nu se scrie: o etichetă lipsă se
 *  vede la click, una suprapusă nu se poate citi deloc.
 */
/** Unde se scrie numele săriturii de lup: DINCOLO DE FUNDUL PUȚULUI, nu în gol.
 *
 *  Golul are 1,00 × 1,30 m, iar numele, scris în el, acoperea cotele (lanțul
 *  «40 / 130 / 40» și adâncimea — v. `coteNivel.coteleIesirii`) și pereții.
 *  Planșa de referință a Oanei (28.09.2026) n-are nume în puț. Se pune deci
 *  în afara lui, în sensul ieșirii (`spre`), după peretele din fund (0,40 m)
 *  și după linia cotei totale (0,50 m mai încolo). Fără `spre`, în centru. */
function numeleIesirii(r: StoreyShape): [number, number] | null {
  const s = r.ala?.spre;
  if (r.kind !== "ala_iesire" || !s || s.length < 2) return null;
  const L = Math.hypot(s[0], s[1]);
  if (!(L > 0)) return null;
  const [sx, sy] = [s[0] / L, s[1] / L];
  const [cx, cy] = centru(r.poly, r.holes);
  let adanc = 0;
  for (const [x, y] of r.poly) adanc = Math.max(adanc, (x - cx) * sx + (y - cy) * sy);
  // …plus jumătatea numelui, scris orizontal pe ecran, pe direcția ieșirii:
  // la un puț care iese spre stânga, textul lat se întindea înapoi peste cote
  const [w, h] = [(r.label?.length ?? 0) * 0.26, 0.55];
  const d = adanc + 0.4 + 0.5 + 0.2 + (w / 2) * Math.abs(sx) + (h / 2) * Math.abs(sy);
  return [cx + sx * d, cy + sy * d];
}

/** Aria scrisă sub numele camerei. La adăpostul ALA e suprafața de adăpostire
 *  — utilă, fără SAS și grup sanitar —, nu a poligonului, care le cuprinde pe
 *  amândouă (Oana, 01.10.2026). */
function ariaAfisata(r: StoreyShape) {
  return r.kind === "ala" && r.ala?.adapostire_m2 != null ? r.ala.adapostire_m2 : (r.area ?? 0);
}

function dispuneEtichete(spatii: StoreyShape[]) {
  const asezate: { x: number; y: number; w: number; h: number }[] = [];
  const out: { r: StoreyShape; x: number; y: number }[] = [];
  const ordonate = [...spatii].sort((a, b) => (a.area ?? 0) - (b.area ?? 0));
  for (const r of ordonate) {
    // Sub 1,4 m pe latura mică nu încape nici numele, nici aria. Textul e în
    // metri, ca tot desenul, deci scala îl duce cu el. Săritura de lup a unui
    // adăpost ALA (1,40 × 1,70 m) își scrie totuși numele: e singurul lucru
    // de pe planșă care spune ce e puțul acela (Oana, 26.09.2026).
    if (r.poly.length < 3 || !r.label
        || (latura(r.poly) < 1.4 && r.kind !== "ala_iesire")) continue;
    const [cx, cy] = numeleIesirii(r) ?? centru(r.poly, r.holes);
    // Lățimea textului, aproximată din numărul de caractere la 0,42 m corp:
    // fără DOM nu se poate măsura, iar aproximarea greșește în siguranță (mai
    // lat decât e).
    const w = r.label.length * 0.26, h = 1.3;
    const loc = [0, -1.4, 1.4, -2.8, 2.8]
      .map((dy) => ({ x: cx, y: cy + dy, w, h }))
      .find((c) => !asezate.some((a) => Math.abs(a.x - c.x) * 2 < a.w + c.w
                                     && Math.abs(a.y - c.y) * 2 < a.h + c.h));
    if (!loc) continue;
    asezate.push(loc);
    out.push({ r, x: loc.x, y: loc.y });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Straturile desenului
// ---------------------------------------------------------------------------
//
// Nu „layerele DWG": alea sunt sute și se cheamă `_02_Y_STRUCTURA`. Astea sunt
// PRODUSELE importului — ce a recunoscut el din desen — și sunt exact lucrurile
// pe care omul vrea să le stingă ca să se uite la restul: parapetele peste
// pereți, structura arhitectului peste compartimentări, etichetele peste tot.

export type Strat = "pereti" | "parapete" | "usi" | "ferestre" | "spatii"
                  | "pereti_structural" | "pereti_party" | "pereti_partition"
                  | "pereti_facade" | "pereti_fire"
                  | "foc"
                  | "materiale" | "adancime"
                  | "placi" | "structura" | "apartamente" | "descarcare"
                  | "repere"
                  | "etichete" | "obiecte" | "parcaj" | "alei" | "rampe_acces" | "rampe_interior"
                  | "nuclee" | "circulatie" | "mobilier"
                  | "cote" | "cote_total" | "cote_axe" | "cote_fatada"
                  | "cote_camere" | "cote_goluri" | "cote_ala"
                  | "scari"
                  | "dedesubt" | "gazde"
                  | "nedesenate";

// PEREȚII, PE ROLUL LOR — și de ce NU pe material
// ---------------------------------------------------------------------------
//
// Ce s-a cerut e „zidărie / rigips / beton, trei filtre". Ce ȘTIE documentul e
// altceva: `Wall.kind` din `canonical_model/kinds.py` e ROLUL peretelui în
// clădire — structural, despărțitor, compartimentare, fațadă, antifoc,
// parapet — și nicăieri, pe niciun emitent, nu scrie din ce e făcut. Un filtru
// „zidărie" ar fi trebuit deci să GHICEASCĂ materialul din rol, iar un perete
// desenat ca rigips fiindcă e subțire e exact minciuna pe care `walls.py` o
// refuză pe față: „WHAT IS NOT DECLARED IS NOT INVENTED".
//
// Așa că filtrarea se face pe ce documentul chiar spune. Când `material` intră
// în schemă, intră ca al doilea nivel lângă ăsta — nu în locul lui: rolul
// rămâne întrebarea structurii, materialul e a devizului, și sunt două
// întrebări, nu două nume pentru una.
export const ROL_PERETE: Readonly<Record<string, Strat>> = {
  structural: "pereti_structural",
  party:      "pereti_party",
  partition:  "pereti_partition",
  facade:     "pereti_facade",
  fire:       "pereti_fire",
  parapet:    "parapete",
};

/** Bifa sub care cade un perete. Un `kind` pe care catalogul nu-l cunoaște —
 *  sau lipsă — cade pe PĂRINTE, nu pe un rol inventat: altfel un perete fără
 *  rol ar fi dispărut din desen odată cu o bifă care nu-l numește. */
export const stratulPeretelui = (kind?: string | null): Strat =>
  ROL_PERETE[kind ?? ""] ?? "pereti";

/** DE CE NU MAI E UN COMUTATOR «Randat» / «Tehnic».
 *
 *  Au fost două desene ale aceluiași nivel: planul de lucru (linie, poché, numele
 *  camerelor scrise) și planul de prezentare (materiale, mobilier, umbre, cifra
 *  în cerc legată de cheia de legendă). A rămas al doilea.
 *
 *  Motivul nu e că unul era mai bun, ci că alegerea nu-și merita butonul. Tot ce
 *  deosebea modurile ținea de CAMERE — iar un subsol de parcaj n-are camere,
 *  deci acolo cele două ieșeau ~95% identice (măsurat: 219 793 vs 208 839 de
 *  caractere de SVG, cu aceleași numere de forme). Două butoane alăturate care
 *  pe jumătate din niveluri nu fac nimic vizibil cer o alegere înainte de a te
 *  uita la desen, și o cer degeaba.
 *
 *  Nimic nu s-a pierdut: ce deosebea modurile a coborât în STRATURI. Numele
 *  camerelor sunt acum bifa „Etichete"; materialele, umbrele și mobilierul sunt
 *  bifele lor. Un mod care ar ține straturile ostatice ar fi însemnat două
 *  locuri care decid ce se vede, iar cele două ar fi divergat. */
/** NUMELE E O CHEIE, nu un text: catalogul de straturi e o listă de MODUL,
 *  citită și de panoul care le bifează, și de numărătoare. Un text scris aici
 *  ar fi înghețat limba la încărcarea modulului; cheia se traduce la desenare —
 *  v. `Vizualizator`, unde rândul se scrie cu `t(s.nume)`. */
export const STRATURI: { cheie: Strat; nume: Cheie; parinte?: Strat }[] = [
  // IERARHIA, declarată aici și nicăieri altundeva: `parinte` spune sub cine
  // stă rândul, iar panoul îl indentează. Stinsul se moștenește — părinte stins
  // înseamnă copii stinși, ORICE ar zice bifa lor — fiindcă altfel „Pereți"
  // dezbifat ar lăsa fațadele pe desen și bifa ar minți.
  //
  // `parapete` era deja o bifă separată, adică deja un rol de perete scos
  // afară; lăsat frate cu „Pereți" acum, același vocabular ar fi stat pe două
  // niveluri deodată.
  { cheie: "pereti", nume: "strat.pereti" },
  { cheie: "pereti_structural", nume: "strat.pereti_structural", parinte: "pereti" },
  { cheie: "pereti_facade", nume: "strat.pereti_facade", parinte: "pereti" },
  { cheie: "pereti_party", nume: "strat.pereti_party", parinte: "pereti" },
  { cheie: "pereti_partition", nume: "strat.pereti_partition", parinte: "pereti" },
  { cheie: "pereti_fire", nume: "strat.pereti_fire", parinte: "pereti" },
  // Betonul etajelor, coborât pe planșeele subsolului. Rând separat fiindcă e
  // un lucru DIFERIT de un perete de subsol, iar numărul lui e cel care făcea
  // planul de parcaj să pară plin de compartimentări inventate.
  //
  // COPIL AL «PEREȚILOR», ca și rolurile de deasupra — a venit frate cu ele,
  // dintr-o vreme când «Pereți» n-avea copii. E tot un rol de perete, iar
  // motivul scris mai sus pentru `parapete` e cuvânt cu cuvânt același: două
  // niveluri pentru același vocabular s-ar citi ca două lucruri diferite.
  //
  // Se suprapune peste `pereti_structural` — o linie de descărcare E un perete
  // `structural`, doar că pe un nivel de parcaj. Filtrul de mai jos întreabă de
  // `descarcare` ÎNAINTEA rolului, deci pe parcaj bifa asta comandă, iar
  // «Structurali» rămâne bifa etajelor. Fără ordinea aia, aceleași 84 de linii
  // s-ar stinge de la două bife și s-ar număra de două ori.
  { cheie: "descarcare", nume: "strat.descarcare",
    parinte: "pereti" },
  { cheie: "parapete", nume: "strat.parapete", parinte: "pereti" },
  // REZISTENȚA LA FOC (ISU): nu un rol, ci o CULOARE peste pereți — clasa de pe
  // obiect (`rezistenta`), în culorile legendei de referință, și griul betonului
  // ALA (v. `rezistentaFoc.ts`). Frate cu «Pereți», nu copil: aprinde și legenda
  // din stânga jos, iar implicitul lui e al NIVELULUI (aprins la subsol, stins
  // la etaje — v. `Vizualizator`), nu al desenului. Al doilea strat al
  // pereților, «Material» (hașura), vine odată cu maparea arhitecților (Oana,
  // 20.10.2026).
  { cheie: "foc", nume: "strat.foc" },
  // Unde ar fi clădirile și unde e limita terenului. Stă lângă «Pereți»
  // fiindcă răspunde la aceeași întrebare ca liniile de descărcare — «cum stă
  // nivelul asta față de ce e deasupra lui» — dar NU e un rol de perete, deci
  // nu e copilul lor.
  { cheie: "repere", nume: "strat.repere" },
  { cheie: "usi", nume: "strat.usi" },
  { cheie: "ferestre", nume: "strat.ferestre" },
  { cheie: "spatii", nume: "strat.spatii" },
  // CELE TREI SUNT COPII AI «SPAȚIILOR», nu frați.
  //
  // Fiindcă asta SUNT: fiecare spune cum se desenează o cameră — cu material pe
  // pardoseală, cu cifra cheii de legendă, cu mobilier — și niciunul nu are ce
  // desena fără camere. Ținute frați, panoul arăta patru rânduri alăturate cu
  // ACELAȘI număr (240, 240, 240, 240 pe un etaj real), care se citeau ca o
  // bifă dublată de trei ori. Numărul e același fiindcă subiectul e același;
  // indentarea o spune, lista plată o ascundea.
  //
  // Aprinse implicit, fiindcă ele SUNT reprezentarea cerută; stinse, planul se
  // întoarce la desenul tehnic curat, care e ce vrea cineva care compară cu o
  // normă.
  { cheie: "materiale", nume: "strat.materiale", parinte: "spatii" },
  // Mobilierul DESENAT în module și copiat în soluție (`Furniture`, 3.14) —
  // nu cel dedus din felul camerei, care nu se mai desenează nicăieri.
  { cheie: "mobilier", nume: "strat.mobilier", parinte: "spatii" },
  // Umbra e a PEREȚILOR — numărul ei e chiar numărul lor — deci stă sub ei.
  { cheie: "adancime", nume: "strat.adancime", parinte: "pereti" },
  { cheie: "placi", nume: "strat.placi" },
  { cheie: "structura", nume: "strat.structura" },
  // NIVELUL DE DEDESUBT, ca fantomă: stâlpii și grinzile etajului imediat
  // inferior, cu linie întreruptă, SUB desenul nivelului. Nu e un obiect al
  // planșeului ăstuia — e proiecția altuia —, de aceea se desenează ca
  // reperele (întrerupt) și stă stins implicit (v. `ascunse` din
  // `Vizualizator`). Răspunde la o singură întrebare: pe ce se poate rezema
  // un stâlp al nivelului ăstuia care nu coboară la fundație.
  { cheie: "dedesubt", nume: "strat.dedesubt" },
  // …și răspunsul propriu-zis: porțiunile axelor grinzilor de dedesubt care
  // trec pe sub un perete PLIN al nivelului curent, departe de reazeme și de
  // stâlpi — cu lungimea utilizabilă scrisă (v. `gazde.ts` pentru reguli).
  // Copil al fantomei: fără grinda dedesubt, porțiunea n-are pe ce sta.
  { cheie: "gazde", nume: "strat.gazde", parinte: "dedesubt" },
  { cheie: "apartamente", nume: "strat.apartamente" },
  { cheie: "etichete", nume: "strat.etichete" },
  // ETICHETELE OBIECTELOR — numele sau tipul lor scris lângă ele, cu linie de
  // indicație: azi tâmplăria adăposturilor ALA («UME1 · prag 15 cm»). Strat
  // separat de `etichete`, care e al NUMELOR CAMERELOR («Camere» în panou):
  // una spune ce e încăperea, cealaltă ce e obiectul (Oana, 30.09.2026).
  { cheie: "obiecte", nume: "strat.obiecte" },
  // Ultimele două nu sunt desenate de `StoreyView`, ci de `PlanView` și
  // `ApartmentView` — dar sunt straturi ale ACELUIAȘI plan, iar catalogul de
  // straturi e unul. Stau în capătul listei fiindcă sunt cele mai bogate: se
  // sting când cineva vrea planul curat.
  //
  // Numele lor spune «— contur» versus ce anume adaugă, fiindcă numărul e
  // ACELAȘI pe amândouă rândurile perechii: 33 de locuri desenate ca
  // dreptunghiuri, aceleași 33 numerotate de planșă. Două rânduri cu «33» și
  // nume care nu se deosebesc ar fi arătat ca o bifă dublată.
  { cheie: "parcaj", nume: "strat.parcaj" },
  // ALEILE CU LĂȚIMEA LOR (5 m), într-un gri puțin mai închis decât platelajul.
  // Axa întreruptă spune pe unde trece aleea; corpul ei spune CÂT ocupă — și
  // asta e întrebarea când te uiți la un gol de lângă ea. Cerut de Oana,
  // 11.09.2026.
  { cheie: "alei", nume: "strat.alei", parinte: "parcaj" },
  // RAMPELE, cu bifa lor. Sunt singurele lucruri de pe planșa de parcaj care
  // nu aparțin nivelului desenat — o rampă traversează două cote — iar când te
  // uiți la cum s-au așezat locurile în jurul stâlpilor, ele acoperă tocmai
  // fâșia despre care e întrebarea. Copil al planșei, nu frate: stinsă
  // planșa, se sting și ele.
  //
  // DOUĂ bife, nu una: pe -1 rampa de acces (parter → -1) și rampa -1 → -2
  // suprapusă (tiparul S) stau una peste alta în plan, iar cu o singură bifă
  // nu puteai vedea niciodată pe cea de dedesubt. Frați sub planșă (arborele
  // straturilor are două niveluri), fiecare cu numărul rampelor ei.
  { cheie: "rampe_acces", nume: "strat.rampe_acces", parinte: "parcaj" },
  { cheie: "rampe_interior", nume: "strat.rampe_interior", parinte: "parcaj" },
  // Ultimele trei nu sunt desenate de `StoreyView`, ci de `ApartmentView` — dar
  // sunt straturi ale ACELUIAȘI plan, iar catalogul de straturi e unul.
  //
  // Trei, nu unul. Erau strânse într-un „Interioare — camere, nuclee, bare"
  // care desena și pereți, și camere, și etichete — toate deja în plan, și
  // pereții la altă grosime decât cea din document. Un strat care redesenează
  // ce e sub el nu e un strat, e o a doua părere. Aici rămâne fiecare lucru pe
  // care planul de arhitectură NU-l are ca vocabular, cu bifa lui:
  // Cotele. Stinse implicit (v. `ascunse` din `Vizualizator`) fiindcă un plan care se
  // deschide cu lanțurile pe el se citește ca o planșă de execuție, iar cel mai
  // des cineva vrea întâi să se UITE. Aprinse, sunt exact desenul de verificare.
  // COTELE, pe patru straturi — fiindcă sunt patru întrebări, iar planșa de
  // referință le desenează pe toate deodată doar fiindcă e A0 tipărit. Pe ecran
  // cele ~1 400 de cifre ale unui etaj se acoperă între ele; separate, fiecare
  // se aprinde când e nevoie de ea.
  { cheie: "cote", nume: "strat.cote" },
  // Cele trei rânduri ale benzii perimetrale, în ordinea de pe planșă: lipit de
  // clădire lanțul fin, apoi axele, apoi totalul cel mai depărtat.
  { cheie: "cote_fatada", nume: "strat.cote_fatada", parinte: "cote" },
  { cheie: "cote_axe", nume: "strat.cote_axe", parinte: "cote" },
  { cheie: "cote_total", nume: "strat.cote_total", parinte: "cote" },
  // Interiorul, în negru — v. `CoteNivel`.
  { cheie: "cote_camere", nume: "strat.cote_camere", parinte: "cote" },
  { cheie: "cote_goluri", nume: "strat.cote_goluri", parinte: "cote" },
  // Adăposturile ALA: SAS-ul, ușile lui, distanțele dintre pereți și stâlpi
  // (Oana, 30.09.2026) — v. `coteNivel.coteleAdaposturilor`.
  { cheie: "cote_ala", nume: "strat.cote_ala", parinte: "cote" },
  { cheie: "nuclee", nume: "strat.nuclee" },
  // Scările de pisică din săriturile de lup (`Ladder`); rândul apare doar pe
  // nivelurile care au vreuna.
  { cheie: "scari", nume: "strat.scari" },
  { cheie: "circulatie", nume: "strat.circulatie" },
  // Nu un produs al importului, ca celelalte, ci întrebarea despre ele: ce ține
  // documentul și desenul nu arată (v. `Fantome`). Rândul se listează doar când
  // există ceva de arătat — pe un document sănătos, zero — deci prezența LUI e
  // deja informația. De asta stă și aprins implicit: o bifă care apare numai
  // când e nevoie n-are de ce să ceară și o apăsare.
  { cheie: "nedesenate", nume: "strat.nedesenate" },
];

/** Câte obiecte are fiecare strat pe nivelul ăsta. Un strat cu zero nu se
 *  listează: o bifă pentru ceva ce nu există pe nivel spune că s-a stins, nu
 *  că lipsește. */
/** E peretele ăsta o linie de descărcare coborâtă din etaje?
 *
 *  Se citește din ce EMIT cele două etape, nu dintr-un câmp inventat pentru
 *  întrebarea asta. Pe un nivel de parcaj există exact două feluri de perete:
 *  `fire`, scris de `canonical._parking_storey` (compartimentare de foc), și
 *  `structural`, scris de `layout.emit._basement_walls` — betonul etajelor,
 *  coborât pe planșeele excavației. Al doilea NU e o compartimentare pe care
 *  ar construi-o cineva la subsol: e o linie de descărcare, purtată peste alei
 *  de grinzi de transfer. Emitentul spune asta în `propagated_from`, dar acel
 *  câmp nu ajunge în browser, iar felul singur nu e de-ajuns — `structural`
 *  există și la etaje, unde e un perete adevărat. Deci discriminantul e
 *  perechea (nivel de parcaj, fel structural).
 *
 *  Erau amestecate într-o singură bifă «Pereți», și de acolo venea întrebarea
 *  «ce caută pereți la subsol?»: 84 de linii de descărcare arătau ca 84 de
 *  pereți desenați acolo.
 *
 *  `plan.parcare`, nu `plan.kind === "parking"`: desenul de mai jos alege poșa
 *  după exact aceeași pereche, iar `canonical.eParcare` spune de ce nu se
 *  încrede numai în `storey_kind` — un subsol vechi vine fără el. Două
 *  răspunsuri la «e nivel de parcaj?» ar fi divergat, și bifa ar fi numărat
 *  altceva decât desenează. */
export const eDescarcare = (plan: StoreyPlan, w: StoreyShape): boolean =>
  plan.parcare && w.kind === "structural";

export function numaraStraturi(plan: StoreyPlan,
                               fantome = 0,
                               dedesubt?: StoreyPlan | null): Record<Strat, number> {
  // Pe rol, dintr-o singură trecere. Părintele numără TOT — el e „pereții
  // nivelului" — iar suma copiilor poate fi mai mică decât el: un perete cu rol
  // necunoscut cade doar pe părinte (v. `stratulPeretelui`), și e mai bine să
  // se vadă diferența decât să i se inventeze un rând.
  //
  // LINIA DE DESCĂRCARE SE ÎNTREABĂ PRIMA, exact ca la desen: ea E un perete
  // `structural`, doar pe un nivel de parcaj, deci numărată și pe rol ar apărea
  // de două ori și ar stinge de la două bife.
  const coteleNivelului = coteNivel(plan);
  const peRol = { pereti_structural: 0, pereti_party: 0, pereti_partition: 0,
                  pereti_facade: 0, pereti_fire: 0, parapete: 0 };
  let descarcare = 0;
  for (const w of plan.walls) {
    if (eDescarcare(plan, w)) { descarcare++; continue; }
    const st = stratulPeretelui(w.kind);
    if (st !== "pereti") peRol[st as keyof typeof peRol]++;
  }
  // Balustradele de sticlă stau sub aceeași bifă ca parapeții zidiți.
  peRol.parapete += (plan.parapete ?? []).length;
  return {
    pereti: plan.walls.length,
    ...peRol,
    descarcare,
    // câți pereți colorează stratul ISU: cei cu o clasă scrisă pe obiect și
    // betonul adăposturilor ALA
    foc: plan.walls.filter((w) => !!culoareRezistenta(w.rezistenta) || eBetonAdapost(w)).length,
    usi: plan.openings.filter((o) => o.tip === "Door").length,
    ferestre: plan.openings.filter((o) => o.tip === "Window").length,
    spatii: plan.spaces.length,
    // Materialul se numără pe camerele care CHIAR primesc unul, nu pe toate:
    // bifa trebuie să spună câte lucruri aprinde. Umbra e a pereților, deci
    // numărul ei e al lor — un nivel fără pereți n-are ce umbri, și atunci
    // rândul nici nu se listează.
    materiale: plan.spaces.filter((r) => !!r.kind).length,
    adancime: plan.walls.length,
    placi: plan.slabs.length,
    structura: plan.columns.length + plan.beams.length,
    apartamente: plan.flatShapes.length,
    etichete: plan.spaces.filter((r) => !!r.label).length
            + plan.flatShapes.filter((f) => !!f.label).length,
    obiecte: plan.openings.filter((o) => eticheteazaGolul(o)).length,
    parcaj: plan.parking,
    // `ramps` sunt obiectele `Ramp` de pe nivel, după fel: `ramp_access` e
    // rampa de acces (parter → -1), `ramp` cea dintre niveluri — exact ce
    // aprinde fiecare bifă (v. `PlanView`).
    alei: plan.lanes.length,
    rampe_acces: plan.ramps.filter((r) => r.kind === "ramp_access").length,
    rampe_interior: plan.ramps.filter((r) => r.kind === "ramp").length,
    // Câte contururi de reper se desenează: fiecare clădire, plus limita
    // terenului când documentul o are. Numărul e al lucrurilor pe care bifa le
    // aprinde, ca la celelalte straturi.
    // `?? []`: un plan parțial sau vechi n-are câmpurile, iar `numaraStraturi`
    // rulează pe orice ajunge în model — v. `try/catch` din `Vizualizator`.
    repere: (plan.refBuildings ?? []).length
          + ((plan.refPlot ?? []).length >= 3 ? 1 : 0),
    // Al DOCUMENTULUI, nu al planșeului: „are documentul obiecte pe care nu le
    // arătăm" nu e o întrebare per nivel, iar numărul n-ar avea ce căuta în
    // `StoreyPlan`. Vine de la apelant, care ține inventarul.
    nedesenate: fantome,
    // Ale nivelului de DEDESUBT, nu ale ăstuia: bifa aprinde barele lui.
    // Fără nivel dedesubt (parterul unei clădiri fără subsol) rândul nu se
    // listează. `?? []`, ca la `repere`: un plan vechi poate veni fără liste.
    dedesubt: dedesubt
      ? (dedesubt.columns ?? []).length + (dedesubt.beams ?? []).length : 0,
    gazde: dedesubt ? gazdePosibile(plan, dedesubt).length : 0,
    // Fiecare număr e al lucrului pe care stratul îl desenează. Compus, numărul
    // era `plan.flats` — adică altceva decât numele stratului promitea.
    nuclee: plan.nuclee,
    mobilier: (plan.mobilier ?? []).length,
    circulatie: plan.bare,
    cote: numaraCote(coteleNivelului),
    cote_fatada: coteleNivelului.fatada.length,
    cote_axe: coteleNivelului.axe.length,
    cote_total: coteleNivelului.total.length,
    cote_camere: coteleNivelului.camere.length,
    cote_goluri: coteleNivelului.goluri.length,
    cote_ala: coteleNivelului.ala.length,
    scari: (plan.ladders ?? []).length,
  };
}

/** O variantă de recompartimentare desenată PESTE apartamentul ei, în cadrul desenului:
 *  cea pe care omul o răsfoiește în `RecompPanou`, sau cea abia aplicată, până când
 *  documentul recitit o are scrisă. */
export interface PrevizRecomp {
  /** id-ul `Flat`-ului peste care se desenează */
  eid: string;
  camere: { fel: string; arie: number; poly: number[][] }[];
  pereti: number[][][];
  usi: number[][][];
  /** ușa de intrare MUTATĂ de solver (segmentul ei) și centrul celei vechi, care nu se
   *  mai desenează — peretele de sub ea rămâne plin */
  intrare?: number[][] | null;
  intrareVeche?: number[] | null;
}

const CULOARE_PREVIZ: Record<string, string> = {
  living: "#f6efd9", bedroom: "#dce9f5", kitchen: "#fbe3cc", bath: "#d6ecf3",
  hall: "#f3e7c9", room: "#eeeeee", niche: "#e4e4e4",
};

export interface StoreyViewProps {
  /** Convenția de desen — v. `PALETA`. Aceleași obiecte, altă foaie de stil. */
  conventie?: Conventie;
  plan: StoreyPlan;
  /** Coridoarele căilor de evacuare (`coridor_evacuare`) se desenează NUMAI cu
   *  stratul «Căi de evacuare» aprins (Oana, 20.10.2026): fără el, fâșiile verzi
   *  rămân ascunse, ca traseele. Lipsă = stins. */
  evacuare?: boolean;
  /** Marchează fiecare formă cu ce ESTE, ca inspectorul să poată numi ce s-a
   *  apăsat. Vezi nota din `inspect.pickAttrs`: niciun handler de click pe
   *  forme, altfel moare panoramarea. */
  interactive?: boolean;
  selected?: Selection | null;
  /** Straturile STINSE. Mulțimea celor ascunse, nu a celor aprinse: implicit
   *  se vede tot, iar un strat nou apărut în catalog se aprinde singur. */
  ascunse?: ReadonlySet<Strat>;
  /** Ce scrie peste un apartament a cărui recompartimentare încă rulează
   *  (`StoreyShape.recomp === "in_lucru"`) — tradus de cine desenează. */
  textInLucru?: string;
  /** Apartamentele de EVIDENȚIAT (id-uri de `Flat`): oglinzile pe care s-ar aplica o
   *  variantă aleasă, cât bifajul din `RecompPanou` e aprins. */
  evidentiate?: ReadonlySet<string>;
  /** Variantele de recompartimentare de desenat peste apartamentele lor (v. `PrevizRecomp`).
   *  Camerele lor acoperă interiorul vechi; numele vechi ale camerelor se sting. */
  previzRecomp?: readonly PrevizRecomp[];
  /** `false` când sub desenul ăsta stă deja planșa de parcaj (`PlanView`), care
   *  și-a pus platelajul, aleile și locurile: hârtia de aici le-ar acoperi.
   *  Pereții, stâlpii și nucleele se desenează atunci PESTE ele, ca elementele
   *  blocante să se vadă unde stau față de alei. */
  hartie?: boolean;
  /** Vederea. Nu e o bifă în plus, fiindcă schimbă CONVENȚIA, nu conținutul:
   *  peretele e un volum alb citit prin umbră, iar numele pleacă în
   *  legendă și pe desen rămâne doar cifra. Sunt două limbaje de desen, iar un
   *  strat nu poate exprima asta — de asta modul e propriu. */
  /** SCARA CURENTĂ, în pixeli pe metru — nu o constantă.
   *
   *  Cu ea se decid pragurile de detaliu: hașura pardoselii și mobilierul se
   *  sting singure când desenul e depărtat, fiindcă acolo nu mai informează, ci
   *  înnegresc. `SitePlanView` a învățat asta pe pielea lui: cu un implicit
   *  fix, toate pragurile treceau mereu, și numerele locurilor se desenau și la
   *  încadrarea pe tot lotul. */
  pxPerM?: number;
  /** NUMELE CAMERELOR ȘI COTELE LE SCRIE ALTCINEVA — `AdnotariNivel`, cu
   *  codul tronsoanelor (Stefan, 23.09.2026: «direct numele camerei la fel cum
   *  e scris și la tronsoane», «cotele cum sunt în tronsoane»). Desenul de aici
   *  le tace atunci pe ale lui, ca să nu fie două rânduri de nume și două de
   *  cote peste aceeași cameră. Lipsă, le scrie singur — pe parcaj, unde
   *  nivelul nu se deschide ca tronson. */
  faraNumeSiCote?: boolean;
  /** Planșeul imediat INFERIOR al aceluiași corp, pentru stratul «Nivelul de
   *  dedesubt» și gazdele posibile. Lipsă = nivelul n-are nimic sub el. */
  dedesubt?: StoreyPlan | null;
}

/** Nuanța fantomei nivelului de dedesubt: gri-albastru, întrerupt. Nu e
 *  culoarea niciunei familii a planului (stâlpul e poșă, peretele alb sau
 *  hașură), ca proiecția să nu poată fi citită drept obiect al nivelului. */
const DEDESUBT = "#6f86a6";
/** Gazda posibilă: verde-smarald, culoare pe care planul n-o folosește —
 *  nici verdictul (roșu), nici întrebarea deschisă (chihlimbar), nici o
 *  materie. E o PROPUNERE de loc, și numai asta. */
const GAZDA = "#0e9f6e";

/** Stâlpii și grinzile nivelului de dedesubt, cu linie întreruptă, și — peste
 *  ele — porțiunile de grindă pe care ar putea sta o pilă a nivelului curent,
 *  cu lungimea lor scrisă. Totul e decor: nu fură clicul planului. */
function NivelDedesubt({ plan, dedesubt, cuGazde }: {
  plan: StoreyPlan; dedesubt: StoreyPlan; cuGazde: boolean;
}) {
  const gazde = useMemo(() => (cuGazde ? gazdePosibile(plan, dedesubt) : []),
                        [plan, dedesubt, cuGazde]);
  return (
    <g pointerEvents="none">
      {(dedesubt.beams ?? []).map((b) => b.poly.length >= 3 && (
        <polygon key={`dd-${b.eid}`} points={puncte(b.poly)} fill="none"
                 stroke={DEDESUBT} strokeWidth={0.9} strokeDasharray="5 3"
                 strokeOpacity={0.85} vectorEffect="non-scaling-stroke" />
      ))}
      {(dedesubt.columns ?? []).map((c) => c.poly.length >= 3 && (
        <polygon key={`dd-${c.eid}`} points={puncte(c.poly)}
                 fill={DEDESUBT} fillOpacity={0.18}
                 stroke={DEDESUBT} strokeWidth={0.9} strokeDasharray="3 2"
                 vectorEffect="non-scaling-stroke" />
      ))}
      {gazde.map((g, i) => {
        const mx = (g.a[0] + g.b[0]) / 2, my = (g.a[1] + g.b[1]) / 2;
        const unghi = (Math.atan2(g.b[1] - g.a[1], g.b[0] - g.a[0]) * 180) / Math.PI;
        // textul se citește de la stânga la dreapta, oricum ar merge grinda
        const rot = unghi > 90 || unghi <= -90 ? unghi + 180 : unghi;
        return (
          <g key={`gz-${g.grinda}-${i}`}>
            <line x1={g.a[0]} y1={g.a[1]} x2={g.b[0]} y2={g.b[1]}
                  stroke={GAZDA} strokeWidth={4} strokeOpacity={0.8}
                  strokeLinecap="butt" vectorEffect="non-scaling-stroke" />
            <text x={mx} y={my} fontSize={0.32} fill={GAZDA} fontWeight={600}
                  textAnchor="middle"
                  transform={`rotate(${rot} ${mx} ${my}) translate(0 -0.22)`}>
              {`${g.L_m.toFixed(2).replace(".", ",")} m`}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function StoreyView({ plan, interactive, selected,
                                     hartie = true, evacuare = false,
                                     ascunse, conventie = "randat",
                                     pxPerM = 40, faraNumeSiCote = false,
                                     dedesubt, textInLucru, evidentiate,
                                     previzRecomp }: StoreyViewProps) {
  const P = PALETA[conventie];
  const tehnic = conventie !== "randat";
  const vede = (s: Strat) => !ascunse?.has(s);
  const pick = (eid: string): PickAttrs => interactive
    ? pickAttrs({ kind: "obiect", level: plan.level, eid }) : {};
  const ales = (eid: string) =>
    !!selected && selected.kind === "obiect" && selected.eid === eid;
  // Cotele au selecția lor («cota»), pe nivel: cheia e strat:index, iar
  // indexul e stabil cât e stabilă geometria — nu e identitate din document.
  const alesCota = (eid: string) =>
    !!selected && selected.kind === "cota" && selected.level === plan.level && selected.eid === eid;

  // PLĂCILE planșeului (`plan.parts`): una de obicei; două deasupra unei bucăți
  // oprite între două care urcă — hârtia se desenează pe fiecare, nu pe un
  // contur întins peste acoperișul ei.
  const placile = plan.parts?.length ? plan.parts : (plan.outline.length >= 3 ? [plan.outline] : []);
  const contur = placile.length ? placile : null;

  // Spațiile, de la mare la mic. Importul citește ȘI zona apartamentului
  // („AP_2 camere", 55 m²), ȘI camerele din ea — două etichete ale
  // arhitectului peste aceeași suprafață. Desenate în ordinea documentului,
  // zona acoperea camerele; desenate mare-întâi, camerele rămân deasupra.
  const spatii = vede("spatii")
    ? [...plan.spaces].filter((r) => evacuare || !eCoridorEvacuare(r))
        .sort((a, b) => (b.area ?? 0) - (a.area ?? 0)) : [];
  // Nucleul — casa scării, ascensoarele, ghena, holul de nivel — se desenează
  // CA NIȘTE CAMERE: pardoseală de beton, simbolul lui (`arhMobilier`), nume și
  // cifră de legendă. Sunt aceleași obiecte pe care subsolul le are ca
  // `Space`-uri; pe etaje vin ca `CorePart` și, netraduse, lăsau un gol alb în
  // mijlocul benzii de apartamente.
  // MOBILIERUL SCRIS ÎN SOLUȚIE (`Furniture`, 3.14), desenat cu simbolurile
  // editorului — aceeași piesă, aceeași linie ca pe masa modulului (Cătălin,
  // 21.10.2026: «exact ca în tronsoane»). Sub pragul de mărire nu se desenează,
  // ca pe masă (`PRAG_MOBILIER`).
  const mobilier = vede("mobilier") && vede("spatii") && pxPerM >= PRAG_MOBILIER
    ? plan.mobilier ?? [] : [];
  // Unde o piesă desenată acoperă nucleul — scara în U din hol, cabina
  // liftului —, simbolul generic al nucleului se retrage: ar sta două scări
  // una peste alta, cea desenată și cea dedusă.
  const SIMBOL_DESENAT = new Set(["scara", "scaraU", "lift", "ghena"]);
  const acoperite = mobilier.filter((p) => SIMBOL_DESENAT.has(p.kind));
  const subPiesa = (r: StoreyShape) => {
    if (!acoperite.length || r.poly.length < 3) return false;
    const cx = r.poly.reduce((s, q) => s + q[0], 0) / r.poly.length;
    const cy = r.poly.reduce((s, q) => s + q[1], 0) / r.poly.length;
    return acoperite.some((p) => {
      const t = (-p.deg * Math.PI) / 180;
      const dx = cx - p.cx, dy = cy - p.cy;
      const u = dx * Math.cos(t) - dy * Math.sin(t), v = dx * Math.sin(t) + dy * Math.cos(t);
      return Math.abs(u) <= p.w / 2 + 0.1 && Math.abs(v) <= p.h / 2 + 0.1;
    });
  };
  const nuclee = vede("nuclee") ? plan.cores.filter((c) => !subPiesa(c)) : [];
  const eNucleu = new Set(plan.cores.map((c) => c.eid));
  // Etichetele se așază pe TOATE spațiile, chiar dacă stratul lor e stins:
  // altfel, stingând „Spații", numele rămase s-ar rearanja — și un plan care
  // își mută cuvintele când stingi altceva se citește ca un bug.
  const cuNume = vede("etichete") && !faraNumeSiCote;
  const cuEtichete = vede("obiecte") && !faraNumeSiCote;
  /** Stratul ISU: pereții în culoarea clasei de rezistență la foc. */
  const cuIsu = vede("foc");
  const tr = useT();
  const tPrag = tr("viz.prag");
  // VARIANTELE DE PE ACEST NIVEL: numai apartamentele lui (aceeași listă trece pe la
  // toate nivelurile desenate)
  const previz = useMemo(() => {
    if (!previzRecomp?.length) return [];
    const aici = new Set(plan.flatShapes.map((f) => f.eid));
    return previzRecomp.filter((p) => aici.has(p.eid));
  }, [previzRecomp, plan.flatShapes]);
  const subPreviz = new Set(previz.map((p) => p.eid));
  // ...dar nu pe zona care se compartimentează încă: acolo scrie textul „în lucru",
  // nu „Hol distribuție" (v. mai jos); nici sub o variantă desenată peste
  const etichete = cuNume
    ? dispuneEtichete([...plan.spaces.filter((r) => r.recomp !== "in_lucru"
                                           && !(r.flat != null && subPreviz.has(r.flat))),
                       ...plan.cores])
      .filter(({ r }) => !eNucleu.has(r.eid) || vede("nuclee"))
    : [];

  const placi = vede("placi")
    ? plan.slabs.filter((s) => s.kind !== "cantilever") : [];
  // COPERTINELE — tavanul unui balcon-cutie de dedesubt, scris de compoziția
  // fațadei pe etajul ăsta, la pardoseală (`rol: copertina`) — separat de
  // celelalte console: pe planul etajului sunt plăci în afara lui, sub planul de
  // secțiune, pe care nu se iese. Desenate doar cu contur, ieșeau drept zone
  // goale lipite de balcoanele etajului, fără să se înțeleagă ce sunt
  // (Cătălin, 7.10.2026): hașurate și scrise «terasă necirculabilă».
  // O SINGURĂ DATĂ pe id: re-aplicările compoziției le-au scris și de patru ori,
  // cu același id (Demo-UTR6), iar hașura și eticheta s-ar fi desenat una peste alta.
  const placiConsola = vede("placi")
    ? plan.slabs.filter((s) => s.kind === "cantilever" && s.rol !== "copertina") : [];
  const cuPlaci = vede("placi");
  const copertine = useMemo(() => {
    if (!cuPlaci) return [];
    const vazute = new Set<string>();
    return plan.slabs.filter((s) => s.rol === "copertina" && s.poly.length >= 3
      && !vazute.has(s.eid) && (vazute.add(s.eid), true));
  }, [plan.slabs, cuPlaci]);
  // Părinte ȘI copil: v. nota de la `STRATURI`. Stinsul se moștenește, deci
  // „Pereți" dezbifat stinge tot, iar un rol dezbifat stinge doar rolul lui.
  const pereti = plan.walls.filter((w) =>
    // `descarcare` ÎNAINTEA rolului — v. nota din `numaraStraturi`: pe parcaj
    // un perete `structural` e o linie de descărcare, și bifa ei comandă.
    // Totul rămâne sub «Pereți»: stinsul se moștenește de la părinte.
    vede("pereti") && (eDescarcare(plan, w) ? vede("descarcare")
                                            : vede(stratulPeretelui(w.kind))));
  // FĂRĂ PEREȚI DEDUȘI (Stefan, 24.09.2026: «nu vrem niciodată să desenăm
  // ceva ce nu există în soluție»). Aici se desena, pe o soluție fără obiecte
  // `Wall`, banda dintre camere ca și cum ar fi un perete. Un document fără
  // pereți se desenează fără pereți.
  // Cotele: măsurate din geometrie, nu citite de pe desen. Derivarea e pură și
  // stă în `coteNivel`, ca să poată fi verificată fără DOM.
  // HAȘURA SE DESENEAZĂ DOAR CÂND SE ȘI VEDE. Sub ~14 px pe metru, un pas de
  // 7 cm iese sub un pixel: liniile se string într-o pată cenușie care doar
  // îngreunează desenul. Peretele rămâne atunci hârtie cu contur — ceea ce e
  // oricum reprezentarea de zidărie din STAS 1434-83, unde rubrica e goală.
  const hasuraVizibila = tehnic;
  // MEMOIZATE PE PLAN (Oana, 20.10.2026): `view` e stare în `Vizualizator`,
  // deci planșa se redesenează la fiecare cadru de pan și zoom, iar cotele se
  // refăceau de fiecare dată — 87 ms pe un subsol cu ALA (razele dintre
  // elemente), adică un scroll sacadat de cum se aprindea bifa. Geometria nu
  // se schimbă când te miști pe hartă; se măsoară o dată pe plan.
  const cotePornite = vede("cote") && !faraNumeSiCote;
  const cote = useMemo(() => (cotePornite ? coteNivel(plan) : CoteGoale), [plan, cotePornite]);
  const vedeCota = (c: Strat) => vede("cote") && vede(c);
  const cotaExt = tehnic ? COTA_INT : COTA_EXT;
  // Umbra e a planului RANDAT: pe o planșă tehnică nu există — acolo peretele
  // e poché, nu un volum luminat.
  const cuAdancime = vede("adancime") && P.culoare;
  // Grupul cu umbră, memoizat pe GEOMETRIE — nu pe selecție. V. nota de la
  // locul unde se desenează.
  /** Scoate culoarea, păstrând tonurile. Declarat o dată, aici, fiindcă e al
   *  convenției tehnice, nu al vreunui obiect. */
  const defsConventie = tehnic ? (
    <defs>
      <filter id="arh-fara-culoare">
        <feColorMatrix type="saturate" values="0" />
      </filter>
    </defs>
  ) : null;
  const grupPereti = useMemo(() => (
    <g filter={cuAdancime ? "url(#arh-umbra-alb)" : undefined}>
      {/* Pe un nivel de PARCAJ pereții structurali se desenează în poșă, ca
          stâlpii, nu albi cu umbră: pe pardoseala gri a garajului un perete
          alb se citește ca o umbră a nimicului, iar pereții de beton coborâți
          din etaje sunt exact elementele blocante după care se așază locurile
          și aleile — cine depanează parcarea trebuie să-i vadă.

          Prin `P.struct`, nu prin `STRUCT`: poșa e a convenției, ca stâlpii
          de dedesubt, deci planșa de noapte n-o primește gri pe negru.
          Ramura TEHNICĂ de mai jos n-are nevoie de cazul ăsta — acolo peretele
          `structural` poartă deja semnul de oțel (v. `HASURA_ROL`), care îl
          deosebește pe hârtie albă mai bine decât o pată. */}
      {pereti.map((w) => {
        if (w.poly.length < 3) return null;
        const p = puncte(w.poly);
        // STRATUL ISU (`cuIsu`): culoarea clasei scrise pe obiect bate orice
        // altă umplere, iar betonul ALA e gri (v. `rezistentaFoc.ts`). Stins,
        // peretele se desenează ca până acum. Pe ambele convenții: culoarea
        // nu e un ton al randării, e o informație a planșei.
        const isu = cuIsu ? culoareRezistenta(w.rezistenta)
                             ?? (eBetonAdapost(w) ? GRI_ALA : undefined)
                          : undefined;
        // puțul săriturii de lup, mic pe ecran: v. `PUT_DISCRET_PX`
        const discret = !isu && ePutDiscret(w, pxPerM);
        if (!tehnic) {
          const umplere = isu ?? (discret ? P.hartie
                          : USOR.has(w.kind ?? "") ? P.pereteUsor
                          : eDescarcare(plan, w) ? P.struct
                          : P.perete);
          const muchie = !isu && w.kind === "fire" ? "var(--fire)" : P.muchie;
          const gros = w.kind === "fire" ? 1.6
                       : w.kind === "facade" ? 1.0 : 0.6;
          // INELAR -> `<path>`, ca gaura să fie gaură. V. `caleCuGoluri`.
          if (w.holes?.length) {
            return (
              <path key={w.eid} d={caleCuGoluri(w.poly, w.holes)}
                    fillRule="evenodd" fill={umplere} stroke={muchie}
                    strokeWidth={gros} vectorEffect="non-scaling-stroke"
                    {...pick(w.eid)} />
            );
          }
          return (
            <polygon key={w.eid} points={p}
                     fill={umplere} stroke={muchie} strokeWidth={gros}
                     vectorEffect="non-scaling-stroke"
                     {...pick(w.eid)} />
          );
        }
        // PLANȘA TEHNICĂ: desen de LINIE. Zidul tăiat e hârtie cu hașură și
        // contur de secțiune — nu o pată plină. Hașura e GEOMETRIE, nu pattern:
        // v. nota din `hasura.ts` despre de ce.
        const nesect = NESECTIONAT.has(w.kind ?? "") || discret;
        // ÎNNEGRIT sub `ZID_INNEGRIT_PX`: plin, cu tuș, fără contur și fără
        // hașură — v. nota de la constantă.
        const innegrit = !nesect && grosimeZid(w) * pxPerM < ZID_INNEGRIT_PX;
        const fel = nesect || innegrit ? null
                  : HASURA_ROL[w.kind ?? ""] ?? HASURA_IMPLICITA;
        // Fiecare fel de hașură dispare la MĂRIMEA LUI: v. `MIN_PX_INTRE_LINII`.
        const d = fel && hasuraVizibila
                  && fel.pas * pxPerM >= MIN_PX_INTRE_LINII
          ? caleHasura(fel.unghiuri.flatMap((u) =>
              liniiHasura(w.poly, fel.pas, u)))
          : "";
        return (
          <g key={w.eid}>
            {/* culoarea ISU bate și înnegrirea: un perete de rampă de 0,20 m
                desenat negru la zoom mic și-ar pierde clasa */}
            <polygon points={p}
                     fill={isu ?? (nesect ? P.hartie : innegrit ? P.tus : P.zid)}
                     stroke={innegrit ? "none" : P.tus}
                     strokeWidth={nesect ? TUS.vazut : TUS.taiat}
                     vectorEffect="non-scaling-stroke" {...pick(w.eid)} />
            {d && (
              <path d={d} stroke={P.tus} strokeWidth={TUS.subtire} fill="none"
                    vectorEffect="non-scaling-stroke" pointerEvents="none" />
            )}
          </g>
        );
      })}
    </g>
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `conventie` E ÎN DEPENDENȚE: grupul e memoizat pe geometrie, iar culoarea
    // peretelui vine din convenție. Fără ea, trecerea Plan <-> 2D lăsa pe ecran
    // pereții desenați în cealaltă convenție — un desen pe jumătate schimbat.
    // `pxPerM` la fel: înnegrirea și hașura se decid pe scara curentă.
  ), [pereti, cuAdancime, interactive, plan.level, conventie,
      plan.parcare, pxPerM, cuIsu]);

  const peretiAlesi = pereti.filter((w) => w.poly.length >= 3 && ales(w.eid));

  // Fără text «EI 120» / «EI 180» lângă perete: clasa o spune culoarea
  // stratului ISU, iar inspectorul o are la click (Oana, 20.10.2026).


  // ușa de intrare veche a unei variante care o mută: nu se desenează (v. `PrevizRecomp`)
  const golAscuns = (o: StoreyShape & { tip: "Door" | "Window" }) => o.tip === "Door"
    && previz.some((p) => p.intrareVeche && o.cx != null && o.cy != null
                    && Math.hypot(o.cx - p.intrareVeche[0], o.cy - p.intrareVeche[1]) < 0.35);
  const goluri = plan.openings.filter((o) =>
    vede(o.tip === "Door" ? "usi" : "ferestre") && !golAscuns(o));
  const grinzi = vede("structura") ? plan.beams : [];
  const stalpi = vede("structura") ? plan.columns : [];
  const apartamente = vede("apartamente") ? plan.flatShapes : [];
  /** Apartamentul -> rangul lui pe nivel, ca spălătura să fie stabilă între
   *  redesenări și diferită între vecini. */
  const indexApartament = useMemo(
    () => new Map(plan.flatShapes.map((f, i) => [f.eid, i])),
    [plan.flatShapes]);
  /** Apartamentul -> tronsonul din care a ieșit, când documentul îl spune. */
  const tronsonApartament = useMemo(
    () => new Map(plan.flatShapes.flatMap((f) =>
      f.tronson ? [[f.eid, f.tronson] as const] : [])),
    [plan.flatShapes]);
  /** Apartamentul -> numărul lui în modul, pe copia unui modul izolat. */
  const apartamentModul = useMemo(
    () => new Map(plan.flatShapes.flatMap((f) =>
      f.apartament != null ? [[f.eid, f.apartament] as const] : [])),
    [plan.flatShapes]);
  /** Ce așteaptă recompartimentarea (v. `StoreyShape.recomp`): camerele apartamentelor
   *  marcate și spațiile comune marcate direct (zona sacrificată de la parter); câte o
   *  etichetă pe apartament, respectiv pe zonă. */
  const inLucruFlats = useMemo(
    () => plan.flatShapes.filter((f) => f.recomp === "in_lucru"), [plan.flatShapes]);
  const inLucruSpatii = useMemo(() => {
    const ale = new Set(inLucruFlats.map((f) => f.eid));
    return plan.spaces.filter((r) => r.poly.length >= 3
      && (r.recomp === "in_lucru" || (r.flat != null && ale.has(r.flat))));
  }, [plan.spaces, inLucruFlats]);
  // Hașura e GEOMETRIE calculată o dată pe document, nu la fiecare redesenare: la
  // zoom planșa se redesenează des, iar sute de linii recalculate de fiecare dată
  // (plus o animație CSS pe ele) făceau toată imaginea să pâlpâie.
  const hasuraCopertine = useMemo(() => copertine.map((s) => ({
    eid: s.eid, poly: s.poly, puncte: puncte(s.poly),
    d: liniiHasura(s.poly, 0.25, 45).map((l) => `M${l[0]} ${l[1]}L${l[2]} ${l[3]}`).join(""),
    eticheta: etichetaPePlaca(s.poly, tr("plan.terasa_necirculabila")),
  })), [copertine, tr]);
  // INTRAREA DE LA TEREN (3.12): placa holului coborât — hașură rară și cota
  // scrisă pe ea —, treptele (liniile contratreptelor, săgeata «urcă», câte
  // sunt) și platforma elevatoare. Pe stratul plăcilor, ca planșeele.
  const placiDenivelate = useMemo(() => (cuPlaci ? plan.slabs : [])
    .filter((s) => s.kind === "offset" && s.poly.length >= 3 && typeof s.sill_m === "number")
    .map((s) => ({
      eid: s.eid, puncte: puncte(s.poly),
      d: liniiHasura(s.poly, 0.4, 135).map((l) => `M${l[0]} ${l[1]}L${l[2]} ${l[3]}`).join(""),
      eticheta: etichetaPePlaca(s.poly, cotaScrisa(s.sill_m as number)),
    })), [plan.slabs, cuPlaci]);
  const treptePlansa = useMemo(() => (cuPlaci ? plan.stairs ?? [] : [])
    .filter((s) => s.poly.length >= 3)
    .map((s) => {
      const n = Math.max(2, Math.round((s.height_m ?? 0.9) / 0.15));
      const linii = liniileTreptelor(s.poly, s.deg ?? 0, n);
      return { eid: s.eid, puncte: puncte(s.poly), n,
               d: linii.map((l) => `M${l[0]} ${l[1]}L${l[2]} ${l[3]}`).join(""),
               sageata: sageataUrca(s.poly, s.deg ?? 0),
               eticheta: etichetaPePlaca(s.poly, tr("plan.trepte_sus", { n })) };
    }), [plan.stairs, cuPlaci, tr]);
  const platformePlansa = useMemo(() => (cuPlaci ? plan.lifts ?? [] : [])
    .filter((s) => s.poly.length >= 3)
    .map((s) => ({ eid: s.eid, puncte: puncte(s.poly), eticheta: etichetaPePlaca(s.poly, tr("plan.platforma")) })),
    [plan.lifts, cuPlaci, tr]);
  const hasuraInLucru = useMemo(() => inLucruSpatii.map((r) => ({
    eid: r.eid, puncte: puncte(r.poly),
    d: liniiHasura(r.poly, 0.45, 45).map((l) => `M${l[0]} ${l[1]}L${l[2]} ${l[3]}`).join(""),
  })), [inLucruSpatii]);
  const inLucruEtichete = useMemo<[string, number[][]][]>(() => [
    ...inLucruFlats.filter((f) => f.poly.length >= 3).map((f) => [f.eid, f.poly] as [string, number[][]]),
    ...plan.spaces.filter((r) => r.recomp === "in_lucru" && r.poly.length >= 3)
      .map((r) => [r.eid, r.poly] as [string, number[][]]),
  ], [inLucruFlats, plan.spaces]);
  /** Inelul obiectului ales, ORICARE ar fi el — cameră, perete, gol, stâlp.
   *
   *  Se caută prin toate familiile fiindcă `data-pick` le marchează pe toate cu
   *  același fel («obiect»), deci selecția poate cădea pe oricare. */
  const inelAles = (() => {
    for (const familie of [spatii, nuclee, pereti, goluri, stalpi, grinzi] as
         { eid: string; poly: number[][] }[][]) {
      const o = (familie ?? []).find((x) => x?.poly?.length >= 3 && ales(x.eid));
      if (o) return o.poly;
    }
    return null;
  })();

  /** Scara ALEASĂ care a fost cumpărată de limita de evacuare, și centrul ei.
   *
   *  Se caută numai printre spații și nuclee, fiindcă doar o casă de scară
   *  poartă câmpul. `null` pentru orice altă selecție — inclusiv pentru un
   *  nucleu al clădirii de deasupra, care n-are câmpul fiindcă există din alt
   *  motiv: scara de etaj are nevoie de acces în subsol oricum.
   *
   *  Pe listele NEFILTRATE, nu pe `spatii`/`nuclee`: explicația e a obiectului
   *  selectat, nu a straturilor aprinse. Cu bifa «Spații» stinsă — cum e
   *  implicit pe un plan de parcaj — căutarea prin listele filtrate n-ar fi
   *  găsit scara pe care omul tocmai a dat click. */
  const evacAleasa = (() => {
    const familii: StoreyShape[][] = [plan.spaces ?? [], plan.cores ?? []];
    for (const familie of familii) {
      for (const o of familie) {
        const d = o.evacDriver;
        if (!d || !ales(o.eid)) continue;
        // Capătul dinspre scară al liniei: centrul declarat, altfel centroidul
        // inelului. O scară fără niciunul nu are de unde porni linia.
        let cx = o.cx, cy = o.cy;
        if ((cx == null || cy == null) && o.poly.length >= 3) {
          cx = o.poly.reduce((t, q) => t + q[0], 0) / o.poly.length;
          cy = o.poly.reduce((t, q) => t + q[1], 0) / o.poly.length;
        }
        if (cx == null || cy == null) continue;
        return { d, cx, cy };
      }
    }
    return null;
  })();

  // Eticheta apartamentului se așază separat de cea a camerelor: sunt
  // suprafețe imbricate, iar amestecate în același evitator o cameră mică ar
  // împinge numele apartamentului afară din el.
  const etAp = cuNume ? dispuneEtichete(plan.flatShapes) : [];

  return (
    <ConventieCtx.Provider value={P}>
    <g>
      {defsConventie}
      {/* amprenta nivelului: hârtia pe care stă desenul */}
      {contur && hartie && contur.map((placa, i) => (
        <g key={`placa-${i}`}>
          <polygon points={puncte(placa)} fill={P.placa}
                   stroke={tehnic ? P.tus : P.muchie}
                   strokeWidth={0.6}
                   vectorEffect="non-scaling-stroke" />
          {/* v. `arh-planseu`: placa neocupată se retrage în fundal, ca banda
              de apartamente să fie subiectul desenului. */}
          {vede("materiale") && P.culoare && pxPerM >= PRAG_HASURA && (
            <polygon points={puncte(placa)} fill="url(#arh-planseu)"
                     pointerEvents="none" />
          )}
        </g>
      ))}

      {placi.map((s) => s.poly.length >= 3 && (
        <polygon key={s.eid} points={puncte(s.poly)} fill={P.placa}
                 stroke="var(--hair, #d8d3c8)" strokeWidth={0.6}
                 vectorEffect="non-scaling-stroke" {...pick(s.eid)} />
      ))}

      {/* PLACA LA ALTĂ COTĂ, TREPTELE ȘI PLATFORMA intrării de la teren (3.12):
          v. `placiDenivelate`. Eticheta ca la copertine. */}
      {placiDenivelate.map((h) => (
        <g key={`dn${h.eid}`}>
          <polygon points={h.puncte} fill={P.placa} stroke={P.muchie} strokeWidth={0.8}
                   vectorEffect="non-scaling-stroke" {...pick(h.eid)} />
          {0.4 * pxPerM >= MIN_PX_INTRE_LINII && (
            <path d={h.d} stroke={P.muchie} strokeOpacity={0.45} strokeWidth={0.6}
                  vectorEffect="non-scaling-stroke" fill="none" pointerEvents="none" />
          )}
          {h.eticheta && (
            <text x={h.eticheta.x} y={h.eticheta.y} fontSize={h.eticheta.marime}
                  transform={`rotate(${h.eticheta.unghi} ${h.eticheta.x} ${h.eticheta.y})`}
                  textAnchor="middle" dominantBaseline="central" fontWeight={500}
                  fill={P.muchie} stroke={P.hartie} strokeWidth={h.eticheta.marime * 0.18}
                  paintOrder="stroke" pointerEvents="none">{h.eticheta.randuri.join(" ")}</text>
          )}
        </g>
      ))}
      {treptePlansa.map((t) => (
        <g key={`tr${t.eid}`}>
          <polygon points={t.puncte} fill={P.hartie} stroke={P.muchie} strokeWidth={0.8}
                   vectorEffect="non-scaling-stroke" {...pick(t.eid)} />
          <path d={t.d} stroke={P.muchie} strokeWidth={0.7} vectorEffect="non-scaling-stroke"
                fill="none" pointerEvents="none" />
          {t.sageata && (
            <path d={`M${t.sageata.coada[0]} ${t.sageata.coada[1]}L${t.sageata.varf[0]} ${t.sageata.varf[1]}`
                     + `M${t.sageata.aripi[0][0]} ${t.sageata.aripi[0][1]}L${t.sageata.varf[0]} ${t.sageata.varf[1]}`
                     + `L${t.sageata.aripi[1][0]} ${t.sageata.aripi[1][1]}`}
                  stroke={P.muchie} strokeWidth={1.1} vectorEffect="non-scaling-stroke" fill="none"
                  strokeLinecap="round" strokeLinejoin="round" pointerEvents="none" />
          )}
          {t.eticheta && (
            <text x={t.eticheta.x} y={t.eticheta.y} fontSize={t.eticheta.marime * 0.8}
                  transform={`rotate(${t.eticheta.unghi} ${t.eticheta.x} ${t.eticheta.y})`}
                  textAnchor="middle" dominantBaseline="central" fontWeight={500}
                  fill={P.muchie} stroke={P.hartie} strokeWidth={t.eticheta.marime * 0.15}
                  paintOrder="stroke" pointerEvents="none">{t.eticheta.randuri.join(" ")}</text>
          )}
        </g>
      ))}
      {platformePlansa.map((l) => (
        <g key={`lf${l.eid}`}>
          <polygon points={l.puncte} fill={P.hartie} stroke={P.muchie} strokeWidth={0.8}
                   strokeDasharray="3 2" vectorEffect="non-scaling-stroke" {...pick(l.eid)} />
          {l.eticheta && (
            <text x={l.eticheta.x} y={l.eticheta.y} fontSize={l.eticheta.marime * 0.8}
                  transform={`rotate(${l.eticheta.unghi} ${l.eticheta.x} ${l.eticheta.y})`}
                  textAnchor="middle" dominantBaseline="central"
                  fill={P.muchie} stroke={P.hartie} strokeWidth={l.eticheta.marime * 0.15}
                  paintOrder="stroke" pointerEvents="none">{l.eticheta.randuri.join(" ")}</text>
          )}
        </g>
      ))}

      {/* NIVELUL DE DEDESUBT, ca fantomă, sub tot ce e al nivelului: v.
          `STRATURI`. Stins implicit; copilul «gazde» stă peste el. */}
      {dedesubt && vede("dedesubt") && (
        <NivelDedesubt plan={plan} dedesubt={dedesubt} cuGazde={vede("gazde")} />
      )}

      {/* Consolele — balcoane, logii — cu linie continuă subțire: plăci care ies
          din corp. Copertinele merg separat, mai jos. COPERTINA INTRĂRII (3.19)
          stă PESTE planul de secțiune al parterului, deasupra decroșului: se
          desenează întreruptă, ca orice proiecție de sus. */}
      {placiConsola.map((s) => s.poly.length >= 3 && (
        <polygon key={s.eid} points={puncte(s.poly)} fill="none"
                 stroke={P.muchie}
                 strokeWidth={0.8}
                 {...(s.rol === "copertina_intrare" ? { strokeDasharray: "6 4" } : {})}
                 vectorEffect="non-scaling-stroke" {...pick(s.eid)} />
      ))}

      {/* COPERTINELE: terasă necirculabilă peste balconul-cutie de dedesubt —
          hașură subțire la 45°, contur, numele scris pe placă. Hașura dispare
          când liniile s-ar strânge sub `MIN_PX_INTRE_LINII`, ca la pereți. */}
      {hasuraCopertine.map((h) => (
        <g key={`cop${h.eid}`}>
          <polygon points={h.puncte} fill="none" stroke={P.muchie} strokeWidth={0.8}
                   vectorEffect="non-scaling-stroke" {...pick(h.eid)} />
          {0.25 * pxPerM >= MIN_PX_INTRE_LINII && (
            <path d={h.d} stroke={P.muchie} strokeOpacity={0.55} strokeWidth={0.6}
                  vectorEffect="non-scaling-stroke" fill="none" pointerEvents="none" />
          )}
          {h.eticheta && (
            <text x={h.eticheta.x} y={h.eticheta.y} fontSize={h.eticheta.marime}
                  transform={`rotate(${h.eticheta.unghi} ${h.eticheta.x} ${h.eticheta.y})`}
                  textAnchor="middle" dominantBaseline="central" fontWeight={500}
                  fill={P.muchie} stroke={P.hartie} strokeWidth={h.eticheta.marime * 0.18}
                  paintOrder="stroke" pointerEvents="none">
              {h.eticheta.randuri.map((r, k) => (
                <tspan key={k} x={h.eticheta!.x}
                       dy={k === 0 ? `${-(h.eticheta!.randuri.length - 1) * 0.625}em` : "1.25em"}>{r}</tspan>
              ))}
            </text>
          )}
        </g>
      ))}

      {/* structura DESENATĂ DE ARHITECT, sub compartimentări: context */}
      {grinzi.map((b) => b.poly.length >= 3 && (
        <polygon key={b.eid} points={puncte(b.poly)} fill="none" stroke={P.struct}
                 strokeWidth={0.8} strokeDasharray="4 2.5"
                 vectorEffect="non-scaling-stroke" {...pick(b.eid)} />
      ))}

      {/* Locurile de parcare NU se desenează aici. `PlanView` — stratul
          «Planșa de parcaj» — le desenează ca locuri: dreptunghi, simbolul
          mașinii și NUMĂRUL lor, din aceleași obiecte. Un contur în plus
          dedesubt era al doilea desen al aceluiași lucru, fără nimic în plus. */}

      {/* camerele: pardoseala pe funcțiune, apoi numele — desenate înaintea
          pereților, ca poché-ul să rămână deasupra și muchiile să iasă curate.
          Materialul e o convenție de reprezentare, nu o declarație despre
          finisaj; v. `arhMaterial`. */}
      {spatii.map((r) => r.poly.length >= 3 && (
        <Pardoseala key={r.eid} points={puncte(r.poly)}
                    kind={vede("materiale") && P.culoare && !eGolAla(r) ? r.kind : undefined}
                    hasura={vede("materiale") && P.culoare && pxPerM >= PRAG_HASURA
                            && !eGolAla(r)}
                    // ADĂPOSTUL ALA E TRANSPARENT: peste el se văd grupul
                    // sanitar, SAS-ul, stâlpii și pereții lui (Oana, 25.09.2026);
                    // la fel ieșirile lui — săriturile de lup sunt goluri între
                    // pereți, nu pardoseli (Oana, 28.09.2026).
                    // `transparent`, nu `none`: se alege tot cu click în el.
                    fond={eCoridorEvacuare(r) ? CORIDOR_EVACUARE
                          : eGolAla(r) ? "transparent"
                          : P.culoare ? undefined : P.hartie}
                    stroke={ales(r.eid) ? "var(--acc, #0b7f92)"
                            : conturZonei(r.kind)?.stroke
                              ?? (tehnic ? "none" : "#ded8cc")}
                    strokeWidth={ales(r.eid) ? 2
                                 : conturZonei(r.kind)?.strokeWidth ?? 0.7}
                    pick={pick(r.eid)} />
      ))}

      {/* Nucleul, cu pardoseala lui de beton — v. `nuclee`. */}
      {nuclee.map((r) => r.poly.length >= 3 && (
        <Pardoseala key={r.eid} points={puncte(r.poly)}
                    kind={vede("materiale") && P.culoare ? r.kind : undefined}
                    hasura={vede("materiale") && P.culoare && pxPerM >= PRAG_HASURA}
                    fond={P.culoare ? undefined : P.hartie}
                    stroke={ales(r.eid) ? "var(--acc, #0b7f92)"
                            : conturZonei(r.kind)?.stroke
                              ?? (tehnic ? "none" : "#ded8cc")}
                    strokeWidth={ales(r.eid) ? 2
                                 : conturZonei(r.kind)?.strokeWidth ?? 0.7}
                    pick={pick(r.eid)} />
      ))}

      {/* APARTAMENTUL, CA SPĂLĂTURĂ PESTE CAMERELE LUI — nu ca linie.
          
          Așa îl arată și planșa de referință (A07, Bd. Timișoara): fiecare
          apartament își primește culoarea lui pastelată peste camere, iar
          conturul nu e desenat nicăieri. Motivul e în date, nu în gust:
          `Flat.poly` e o ANVELOPĂ, nu perimetrul apartamentului — măsurat pe
          documentul real, 10,8% din inelul unui apartament nu e acoperit de
          camerele lui, iar pe unul 26,6%. Desenată ca linie, anvelopa taie
          diagonal prin camere și pune o graniță unde nu e niciun perete.
          
          Spălătura spune exact ce documentul chiar știe — `Space.flat`, adică
          «camerele astea sunt un apartament» — și nimic în plus.
          
          CULOAREA VINE DIN TRONSON, când documentul spune din ce modul a ieșit
          apartamentul: tot ce a ieșit din M1 e la fel colorat, în toate
          blocurile, ca să se vadă dintr-o privire că modulul se repetă
          identic — v. `culoareaTronsonului`.
          
          Fără tronson se cade înapoi pe INDEXUL apartamentului pe nivel, care
          nu spune nimic despre apartament dar garantează că doi vecini nu
          împart culoarea. Documentele emise înainte ca generatorul să scrie
          modulul îl au totuși în etichetă, iar `tronsonulApartamentului` îl
          citește de acolo — deci calea asta rămâne pentru documentele care
          chiar n-au de unde ști. */}
      {apartamente.length > 0 && spatii.map((r) => {
        const i = indexApartament.get(r.flat ?? "");
        const t = tronsonApartament.get(r.flat ?? "");
        return i == null || r.poly.length < 3 ? null : (
          <polygon key={`ap${r.eid}`} points={puncte(r.poly)}
                   fill={culoareaApartamentului(t, i, apartamentModul.get(r.flat ?? ""))}
                   stroke="none" pointerEvents="none" />
        );
      })}

      {/* CE SE COMPARTIMENTEAZĂ ÎNCĂ: apartamentele marcate „în lucru" de pasul 1 și, la
          parter, zona unei locuințe sacrificate rămasă hol comun. Jobul de
          recompartimentare le rescrie în loc peste câteva minute, iar documentul se
          reîncarcă singur. Până atunci: hașură, gri și un text care pulsează — nu se
          citesc ca o compartimentare gata. */}
      {hasuraInLucru.map((h) => (
        <g key={`il${h.eid}`} pointerEvents="none">
          <polygon points={h.puncte} fill="#8a8a8a" fillOpacity={0.35} stroke="none" />
          <path d={h.d} stroke="#5a5a5a" strokeWidth={0.03} fill="none" />
        </g>
      ))}
      {evidentiate && plan.flatShapes.map((f) => f.poly.length >= 3 && evidentiate.has(f.eid) && (
        <polygon key={`ev${f.eid}`} points={puncte(f.poly)} fill="var(--acc, #0b7f92)" fillOpacity={0.18}
                 stroke="var(--acc, #0b7f92)" strokeWidth={2.2} vectorEffect="non-scaling-stroke"
                 pointerEvents="none" />
      ))}
      {inLucruEtichete.map(([eid, poly]) => {
        const [cx, cy] = centru(poly);
        return (
          <text key={`ilt${eid}`} x={cx} y={cy} fontSize={0.42} fontWeight={600}
                textAnchor="middle" dominantBaseline="central"
                fill="#ffffff" stroke="#4a4a4a" strokeWidth={0.06} paintOrder="stroke"
                pointerEvents="none">{textInLucru ?? "…"}</text>
        );
      })}

      {/* MOBILA NU SE DESENEAZĂ: nu e în soluție. Era dedusă aici din felul
          camerei — canapea în living, pat în dormitor —, deci nu se putea
          alege, muta sau șterge, și nu exista pentru niciun motor (Stefan,
          23.09.2026: «dacă e fake, lipsește din soluție, mai bine o scoatem»).
          Aceeași regulă ca pe masă: «dacă mobila nu e desenată în soluție nu o
          desenăm». Rămân SIMBOLURILE nucleului, mai jos — scara, liftul,
          ghena —, care spun ce e spațiul, nu cum s-ar mobila. */}
      {/* Simbolul nucleului — scara cu două rampe, cabina liftului, țevile
          ghenei: e semnul prin care se recunoaște nucleul, nu o piesă care
          s-ar putea muta. */}
      <g style={tehnic ? SCHITA_MOBILIER : undefined}>
        {[...nuclee, ...spatii.filter((r) => !!r.kind && SIMBOL_NUCLEU.has(r.kind) && !subPiesa(r))]
          .map((r) => (
            <Mobilier key={`m${r.eid}`} poly={r.poly} kind={r.kind} deg={r.deg}
                      pxPerM={pxPerM} clipId={`arh-cam-${r.eid}`}
                      plan={tehnic} />
          ))}
        {/* Casa de scară de evacuare a parcajului (§35): treptele din rampele ei, în
            coordonatele planului, ca scara desenată din mobilier. */}
        {spatii.filter((r) => r.scaraEvacuare).map((r) => (
          <ScaraDinRampe key={`se${r.eid}`} rampe={r.scaraEvacuare!.rampe}
                         latime={r.scaraEvacuare!.latime_m} hNivel={INALTIME_SUBSOL_M}
                         adancPodest={r.scaraEvacuare!.podest_m}
                         treapta={r.scaraEvacuare!.treapta_m} pas={r.scaraEvacuare!.pas_m} />
        ))}
      </g>

      {/* MOBILIERUL DESENAT: fiecare piesă în cadrul ei — translată în centru,
          rotită, oglindită după rotire (`scale`) — exact ca pe masa modulului
          (`tronson/Desen`): covoarele dedesubt, aparatele peste blaturi
          (`rangDesen`), scara trasă din rampele ei. Grosimea liniei e cea a
          planșei, cu același factor de mărire ca pe masă. */}
      {mobilier.length > 0 && (
        <g className="mobilier-solutie"
           style={{ ...(tehnic ? SCHITA_MOBILIER : {}),
                    "--mob-gros": Math.min(1, Math.max(0.3, pxPerM / 40)) } as React.CSSProperties}
           pointerEvents="none">
          {[...mobilier]
            .sort((a, b) => (a.rang ?? rangDesen(a.kind)) - (b.rang ?? rangDesen(b.kind)))
            .map((p) => {
              const adu = (q: [number, number]): [number, number] => [q[0], q[1]];
              const piesa = p.scara
                ? <ScaraDinRampe rampe={p.scara.rampe.map((r) => ({ de: adu(r.de), la: adu(r.la) }))}
                                 latime={p.scara.latime_m} hNivel={INALTIME_NIVEL_M}
                                 adancPodest={p.scara.podest_m}
                                 podeste={podesteleScarii(p.scara)}
                                 treapta={p.scara.treapta_m} pas={p.scara.pas_m} />
                : piesaSingura(p.kind, p.w, p.h, { hNivel: INALTIME_NIVEL_M })
                  ?? piesaPlan(p.kind, p.w, p.h);
              if (!piesa) return null;
              const ogl = p.ogl[0] || p.ogl[1]
                ? ` scale(${p.ogl[0] ? -1 : 1} ${p.ogl[1] ? -1 : 1})` : "";
              return (
                <g key={`fn${p.eid}`} data-fel={p.kind}
                   transform={`translate(${p.cx} ${p.cy}) rotate(${p.deg})${ogl}`}>
                  {piesa}
                </g>
              );
            })}
        </g>
      )}


      {/* OBIECTELE SANITARE ALE ADĂPOSTURILOR ALA — WC-urile din cabine și
          lavoarele din tampon, scrise în soluție de pasul ALA al parcării
          (`props["architecture.ala"]`), deci desenate: nu sunt deduse din felul
          camerei. Spatele fiecăruia e spre peretele lui. */}
      <g style={tehnic ? SCHITA_MOBILIER : undefined} pointerEvents="none">
        {spatii.flatMap((r) => (r.ala?.obiecte ?? []).map((ob, i) => {
          const c = r.ala?.obiecte_c?.[i], d = r.ala?.obiecte_dir?.[i];
          if (!c || !d) return null;
          const unghi = (Math.atan2(d[0], -d[1]) * 180) / Math.PI;
          return (
            <g key={`ala${r.eid}-${i}`}
               transform={`translate(${c[0]} ${c[1]}) rotate(${unghi})`}>
              {piesaSingura(ob.fel, ob.w, ob.h)}
            </g>
          );
        }))}
      </g>

      {/* Pereții, cu umbra lor pe pardoseală: un singur filtru pe GRUP, nu
          câte unul pe perete — v. `arh-umbra`. Umbra e ce ridică planul de la
          schemă la planșă, fiindcă separă ce e tăiat de ce e pardoseală. */}
      {/* GRUPUL FILTRAT NU CUNOAȘTE SELECȚIA — și asta e o cerință de viteză,
          nu o preferință de stil.
          Filtrul `arh-umbra-alb` are DOUĂ `feDropShadow` și e pus pe un singur
          grup, cu toți pereții nivelului. Regiunea lui e 128% din cadrul
          grupului — adică, la zoom mare, zeci de mii de pixeli pe latură.
          Browserul o rasterizează din nou ORI DE CÂTE ORI se schimbă ceva
          înăuntru, iar două neclarități gaussiene peste suprafața aia țin
          secunde bune, nu milisecunde.

          Înainte, conturul peretelui ales se desena CHIAR ÎN grup (`ales(w.eid)`
          schimba `stroke` și `strokeWidth`). Deci fiecare click pe orice obiect
          rescria grupul și punea browserul să refacă umbrele întregului nivel —
          de aici veneau secundele (până la minute, pe un ansamblu cu toate
          corpurile) dintre click și evidențiere.

          Acum grupul depinde numai de geometrie, e memoizat, iar selecția se
          desenează peste el. Rasterizarea filtrului rămâne în cache.
      */}
      {grupPereti}

      {/* BALUSTRADELE BALCOANELOR, din document (`Railing`). Stăteau doar în
          3D și în elevație, fiindcă sunt obiecte ale fațadei — iar pe plan
          balconul ieșea o placă fără nimic pe margine, deși balustrada exista
          (măsurat pe Demo-Daniel-Olivia, 23.09.2026: toate cele 64 de balcoane
          ale etajului 1 aveau una). E o linie, nu un panou: nu acoperă nimic.
          Sub bifa parapeților, fiindcă ASTA e: parapetul balconului, de
          sticlă în loc de zidit. */}
      {vede("pereti") && vede("parapete") && (plan.parapete ?? []).map((r) => (
        <g key={r.eid} {...pick(r.eid)}>
          {/* GROSIMEA EI, în metri: la zoom mare linia de 3,2 px e mai subțire
              decât balustrada, iar îmbinarea cu despărțitorul (fața lui pe fața
              ei, `fatada_reguli._lipeste_despartitoarele`) părea o fantă.
              Jardiniera și parapetul plin stau de o parte a axului: rămân linie. */}
          {r.kind !== "planter" && r.kind !== "solid" && (
            <polyline points={puncte(r.axis)} fill="none" stroke={P.tus}
                      strokeWidth={r.kind === "screen" ? Math.max(r.thickness_m ?? 0.05, 0.03) : 0.03}
                      strokeLinejoin="miter" />
          )}
          <polyline points={puncte(r.axis)} fill="none"
                    stroke={P.tus}
                    strokeWidth={3.2} strokeLinejoin="miter"
                    vectorEffect="non-scaling-stroke" />
          <polyline points={puncte(r.axis)} fill="none"
                    stroke={ales(r.eid) ? "var(--acc, #c05a33)" : P.hartie}
                    strokeWidth={1.4} strokeLinejoin="miter"
                    vectorEffect="non-scaling-stroke" pointerEvents="none" />
        </g>
      ))}

      {/* REPERELE: unde ar fi clădirile, și limita terenului.
          Un subsol săpat până la 2 m de limita de proprietate depășește cu mult
          amprentele de deasupra, iar fără repere planul lui nu se poate citi —
          nu se vede nici cât iese din clădiri, nici cât mai e până la vecin.

          ÎNTRERUPT, fiindcă e o proiecție din alt plan, nu un contur al acestui
          nivel: aceeași convenție ca `SiteLayer.balconies`. Oglinda există deja
          — `SiteLayer.excavation` desenează subsolul pe planșa de situație.

          DEASUPRA, nu dedesubt: un reper desenat sub pereți s-ar ascunde exact
          acolo unde amprenta atinge un perete, adică unde relația e cea mai
          interesantă. Subțire și palid, ca să nu concureze cu desenul.

          `pointerEvents="none"` pe tot: sunt context, nu obiecte, iar clicul
          trebuie să treacă la ce e dedesubt. */}
      {vede("repere") && (
        <g pointerEvents="none" stroke={REPER} fill="none"
           strokeOpacity={0.85} strokeLinejoin="round">
          {/* GROSIMEA E ÎN PIXELI, prin `vectorEffect` PE FIECARE POLIGON.
              Prima versiune îl punea pe grup — dar `vector-effect` NU e o
              proprietate moștenită în SVG, se aplică per element. Deci n-a avut
              niciun efect, iar cifrele au fost citite ca METRI: 1,1 m ≈ 8 px la
              25 m pe ecran, adică dungi mai grase decât pereții.

              Pixeli, nu metri, deși planșa de situație își desenează limita de
              proprietate în metri (0,25 m) — și diferența e o alegere. Un reper
              trebuie să rămână firav la ORICE scară: în metri ar arăta bine pe
              tot lotul și ar ajunge 10 px la un zoom pe detaliu, unde e cel mai
              mult nevoie să nu concureze cu desenul. Restul lui `StoreyView`
              lucrează oricum în pixeli — pereții 0,6, inelul de selecție 2,5. */}
          {(plan.refPlot ?? []).length >= 3 && (
            <polygon points={puncte(plan.refPlot)} strokeWidth={1.2}
                     strokeDasharray="9 3 2 3"
                     vectorEffect="non-scaling-stroke" />
          )}
          {(plan.refBuildings ?? []).map((r, i) => (
            <polygon key={`rb${i}`} points={puncte(r.poly)} strokeWidth={1}
                     strokeDasharray="5 3"
                     vectorEffect="non-scaling-stroke" />
          ))}
          {/* Codul corpului, în centrul amprentei lui. Pe un subsol comun sub
              mai multe volume, «C1» lângă un contur punctat e diferența dintre
              «iese din clădiri» și «iese din CARE clădire».
              Se stinge la depărtare, ca eticheta de corp de pe planșa de
              situație: un text de 0,7 m sub 8 px nu se citește, doar murdărește
              desenul. */}
          {REPER_COD * pxPerM >= 8 && (plan.refBuildings ?? []).map((r, i) => {
            if (!r.code) return null;
            const cx = r.poly.reduce((t, q) => t + q[0], 0) / r.poly.length;
            const cy = r.poly.reduce((t, q) => t + q[1], 0) / r.poly.length;
            return (
              <text key={`rc${i}`} x={cx} y={cy} fontSize={REPER_COD} fill={REPER}
                    stroke="none" opacity={0.7} textAnchor="middle"
                    dominantBaseline="central">{r.code}</text>
            );
          })}
        </g>
      )}

      {/* Conturul peretelui ales, DEASUPRA grupului filtrat. Singura diferență
          vizibilă față de înainte: muchia aleasă nu mai primește umbră — ceea ce
          pentru un contur de 2 px nici nu se vedea. */}
      {peretiAlesi.map((w) => (
        <polygon key={`sel${w.eid}`} points={puncte(w.poly)}
                 fill="none" stroke="var(--acc, #0b7f92)" strokeWidth={2}
                 vectorEffect="non-scaling-stroke" pointerEvents="none" />
      ))}

      {/* PERETELE CORTINĂ (format 3.10): pe fața lui nu mai e zid, deci planul
          îl desenează ca pe o tâmplărie — fața dinăuntru și cea a capacelor,
          linia sticlei între ele și montanții (Cătălin, 16.10.2026: «să am un
          contur și pentru fațada cortină, o linie care să zică fațadă cortină»).
          Nu și cortina de peste atic: ea stă pe același etaj, dar deasupra
          tăieturii — v. `panouPesteSectiune`. */}
      {vede("ferestre") && (plan.panouri ?? [])
        .filter((p) => p.kind === "curtain_wall" && p.cortina && p.n && !panouPesteSectiune(p))
        .map((p) => <CortinaInPlan key={p.eid} p={p} pick={pick(p.eid)} ales={ales(p.eid)} />)}

      {/* Golurile, PESTE perete: fâșia lor se umple cu hârtie — asta e „taie
          peretele" — și primește semnul tâmplăriei. */}
      {goluri.map((o) => (
        <Gol key={o.eid} o={o} pick={pick(o.eid)} ales={ales(o.eid)} />
      ))}

      {/* Scările de pisică ale săriturilor de lup: dreptunghiul treptelor pe
          peretele din fund, cu treptele văzute de sus. */}
      {vede("scari") && (plan.ladders ?? []).map((l) => l.poly.length >= 3 && (
        <ScaraDePisica key={l.eid} l={l} pick={pick(l.eid)} ales={ales(l.eid)} />
      ))}

      {stalpi.map((c) => c.poly.length >= 3 && (
        <polygon key={c.eid} points={puncte(c.poly)}
                 fill={tehnic ? P.tus : P.struct}
                 stroke={ales(c.eid) ? "var(--acc, #0b7f92)" : "none"}
                 strokeWidth={2} vectorEffect="non-scaling-stroke"
                 {...pick(c.eid)} />
      ))}

      {/* VARIANTA DE RECOMPARTIMENTARE, PESTE APARTAMENT: camerele ei acoperă
          interiorul vechi (pereți despărțitori, uși, pardoseli), apoi pereții și
          ușile ei, cu numele și aria fiecărei camere. Conturul punctat spune că e o
          variantă privită, nu (încă) planul scris. */}
      {previz.map((p) => (
        <g key={`pv${p.eid}`} pointerEvents="none">
          {p.camere.map((c, i) => c.poly.length >= 3 && (
            <polygon key={`c${i}`} points={puncte(c.poly)}
                     fill={tehnic ? P.hartie : (CULOARE_PREVIZ[c.fel] ?? "#eeeeee")}
                     stroke={tehnic ? P.tus : "#8a8a8a"} strokeWidth={0.6}
                     vectorEffect="non-scaling-stroke" />
          ))}
          {p.pereti.map((w, i) => w.length >= 3 && (
            <polygon key={`w${i}`} points={puncte(w)} fill={tehnic ? P.tus : "#4a4a4a"} stroke="none" />
          ))}
          {p.intrare && p.intrare.length >= 2 && (
            <IntrareMutata seg={p.intrare} camere={p.camere} hartie={P.hartie} muchie={P.tus} />
          )}
          {p.usi.map((u, i) => u.length >= 2 && (
            <line key={`u${i}`} x1={u[0][0]} y1={u[0][1]} x2={u[1][0]} y2={u[1][1]}
                  stroke="var(--acc, #0b7f92)" strokeWidth={3} strokeLinecap="butt"
                  vectorEffect="non-scaling-stroke" />
          ))}
          {plan.flatShapes.filter((f) => f.eid === p.eid && f.poly.length >= 3).map((f) => (
            <polygon key="contur" points={puncte(f.poly)} fill="none"
                     stroke="var(--acc, #0b7f92)" strokeWidth={2} strokeDasharray="6 4"
                     vectorEffect="non-scaling-stroke" />
          ))}
          {cuNume && p.camere.map((c, i) => {
            if (c.poly.length < 3 || latura(c.poly) < 1.4) return null;
            const [cx, cy] = centru(c.poly);
            const cheie = `recomp.fel.${c.fel}` as Cheie;
            return (
              <text key={`t${i}`} x={cx} y={cy} fontSize={0.36} textAnchor="middle"
                    dominantBaseline="central" fill={P.nume}>
                <tspan x={cx} dy="-0.25em">{esteCheie(cheie) ? tr(cheie) : c.fel}</tspan>
                <tspan x={cx} dy="1.2em" opacity={0.75}>{c.arie.toFixed(1)} m²</tspan>
              </text>
            );
          })}
        </g>
      ))}

      {/* Numele apartamentului, în cărămiziu apăsat: e eticheta unității, nu a
          unei camere, și pe un etaj cu amândouă trebuie să se vadă care e
          care. */}
      {cuNume && etAp.map(({ r, x, y }) => (
        <text key={`a${r.eid}`} x={x} y={y} fontSize={0.5} textAnchor="middle"
              dominantBaseline="central" fill={P.nume} fontWeight={600}
              pointerEvents="none" opacity={0.75}>{r.label}</text>
      ))}

      {/* TÂMPLĂRIA ADĂPOSTULUI, cu indicativul din normativ și pragul ei, scrise
          lângă gol și legate de el cu o linie de indicație — ca pe planșa de
          referință («um.1 / prag 15 cm»). Pe stratul «Etichete» — cel al
          numelui sau tipului OBIECTELOR, altul decât «Camere», care scrie
          numele camerelor (Oana, 30.09.2026). Ușile simple (UE, ale grupului
          sanitar și ale cabinelor) nu se etichetează deloc; indicativul lor
          rămâne în date. Ușa SAS-ului se desenează ca orice ușă — același
          semn ca la apartamente —, doar eticheta o deosebește. */}
      {cuEtichete && goluri.filter(eticheteazaGolul).map((o) => (
        <EticGol key={`eg${o.eid}`} o={o} text={textulGolului(o, tPrag)} />
      ))}
      {/* EMBLEMA DE PE GOL (Oana, 20.10.2026): cercul în culoarea clasei, cu
          «EI₂ / 60-C» pe ușile cu rezistență, «UM» pe UME, «OM» pe oblon — ca
          pe planșa de referință. Pe același strat cu etichetele obiectelor;
          eticheta cu indicativul și pragul rămâne alături, emblema vine peste
          ușă. */}
      {cuEtichete && goluri.map((o) => {
        const e = emblemaGolului(o);
        return e && <EmblemaGol key={`em${o.eid}`} o={o} emblema={e} />;
      })}

      {/* etichetele, la urmă: nimic nu are voie să scrie peste ele */}
      {cuNume && etichete.map(({ r, x, y }) => (
        <g key={`t${r.eid}`} pointerEvents="none">
          <text x={x} y={y} fontSize={0.42} textAnchor="middle"
                dominantBaseline="central" fill={P.nume}
                stroke={tehnic ? "none" : HARTIE}
                strokeWidth={tehnic ? 0 : 0.09} paintOrder="stroke"
                strokeLinejoin="round">{r.label}</text>
          {/* fără arie la săritura de lup: e un puț, nu o încăpere */}
          {!!r.area && r.area > 1 && r.kind !== "ala_iesire" && (
            <text x={x} y={y + 0.52} fontSize={0.32} textAnchor="middle"
                  dominantBaseline="central" fill={INK}
                  stroke={tehnic ? "none" : HARTIE}
                  strokeWidth={tehnic ? 0 : 0.075} paintOrder="stroke"
                  strokeLinejoin="round">
              {ariaAfisata(r).toFixed(2)} m²
            </text>
          )}
          {/* pe planșă rămân doar numele și aria; socoteala necesarului
              (volum ÷ înălțime × persoane) stă în inspector, la click
              (Oana, 19.10.2026) */}
        </g>
      ))}

      {/* CONTURUL SELECȚIEI, DESENAT ULTIMUL.
          Camera aleasă primea deja culoarea de accent pe conturul ei
          (`Pardoseala`), dar conturul unei camere trece exact pe unde trec
          pereții ei — iar banda de perete se desenează DEASUPRA. Rezultatul:
          evidențierea exista în DOM și nu se vedea nicăieri, ceea ce arată
          identic cu «nu s-a selectat nimic». Deasupra tuturor, se vede.
          `pointerEvents="none"`, ca să nu fure clicul următor. */}
      {inelAles && (
        <polygon points={puncte(inelAles)} fill="none"
                 stroke="var(--acc, #0b7f92)" strokeWidth={2.5}
                 vectorEffect="non-scaling-stroke" pointerEvents="none" />
      )}

      {/* COTELE, ULTIMELE. O cotă e o adnotare PESTE desen, nu un lucru din
          clădire: trece peste orice și nu fură niciun click (`Cota` e `DECOR`).
          
          Convenția e a planșei de referință — v. `Cota`: interiorul negru,
          banda perimetrală albastră, și aceeași mărime de cifră pe toate
          rândurile benzii, fiindcă nici pe A07 totalul nu se scrie mai mare
          decât restul. Nu mai trec prin `DimSpanView` (planșa de situație):
          acolo o cotă e rară și se vrea văzută de departe, aici sunt o mie. */}
      {/* Pe planșa TEHNICĂ toate cotele sunt negre: un desen declarat alb-negru
          n-are voie să aibă o familie colorată, oricât de convenabil ar fi
          albastrul ca s-o deosebești de clădire. Pe „2D" rămâne albastru
          perimetral / negru interior, ca pe planșa de referință. */}
      {/* CLICK PE CIFRĂ (Oana, 20.10.2026): cota devine selecția («cota»,
          pe cheia strat:index), iar `Cota` o desenează aprinsă, cu liniile ei
          de capăt, ca să se vadă ce măsoară numărul. Al doilea click sau unul
          pe pânză o sting, ca la orice obiect (`Vizualizator.ridică`). */}
      {([
        ["cote_goluri", "goluri", cote.goluri, 0.3, COTA_INT],
        ["cote_camere", "camere", cote.camere, 0.34, COTA_INT],
        ["cote_ala", "ala", cote.ala, 0.3, COTA_INT],
        // Holurile căilor de evacuare și ieșirile adăposturilor ALA se cotează
        // ODATĂ CU COTELE, fără bifă a lor: golul săriturii de lup e ce verifică
        // normativul (Oana, 28.09.2026), iar lumina holului la fel (20.10.2026).
        ["cote", "evacuare", cote.evacuare, 0.3, COTA_INT],
        ["cote", "iesiri", cote.iesiri, 0.34, COTA_INT],
        ["cote_fatada", "fatada", cote.fatada, 0.38, cotaExt],
        ["cote_axe", "axe", cote.axe, 0.38, cotaExt],
        ["cote_total", "total", cote.total, 0.38, cotaExt],
      ] as const).map(([strat, cheie, spans, fs, culoare]) =>
        (strat === "cote" ? vede("cote") : vedeCota(strat)) && spans.map((d, i) => {
          // doar valori simple și `d` din lista memoizată: `Cota` e `memo`, și
          // un obiect nou pe cadru (atributele de selecție) i-ar anula memoizarea
          const eid = `${cheie}:${i}`;
          return <Cota key={`c-${eid}`} d={d} fs={fs} culoare={culoare}
                       level={interactive ? plan.level : undefined} eid={eid}
                       ales={alesCota(eid)} />;
        }))}
      {/* DE CE A FOST NEVOIE DE SCARA ASTA, cât e ea selectată.
          Desenat numai la selecție: e o explicație, nu un strat al planșei, iar
          lăsat mereu aprins ar adăuga marcaje pe un desen care are deja destule.

          DOUĂ TRASEE MERSE, pe alei, nu linii drepte — și diferența e tot
          rostul desenului. Linia dreaptă MINTE: măsurat pe o planșă reală,
          punctul stătea la 40,3 m în linie dreaptă de scara lui, dar la 46,0 m
          DE MERS de următoarea ieșire, fiindcă drumul ocolește. Un punct care
          pare lângă o scară poate fi dincolo de un perete de compartiment.

            ROȘU  — drumul până la cea mai apropiată ieșire care exista DEJA:
                    cel care depășea limita. Ăsta e argumentul.
            VERDE — drumul până la scara asta: cel care îl aduce în limită.

          Roșul se desenează primul, ca verdele să rămână deasupra pe porțiunea
          comună — ce s-a câștigat trebuie să fie ce se vede.

          `pointerEvents="none"` pe tot, ca să nu fure clicul următor. */}
      {evacAleasa && (
        <g pointerEvents="none" fill="none" strokeLinejoin="round"
           strokeLinecap="round">
          {evacAleasa.d.path_old.length >= 2 && (
            <polyline points={puncte(evacAleasa.d.path_old)}
                      stroke={EVAC_LUNG} strokeWidth={2} strokeOpacity={0.85}
                      strokeDasharray="6 4"
                      vectorEffect="non-scaling-stroke" />
          )}
          {evacAleasa.d.path_new.length >= 2 && (
            <polyline points={puncte(evacAleasa.d.path_new)}
                      stroke={EVAC_SCURT} strokeWidth={2} strokeOpacity={0.9}
                      vectorEffect="non-scaling-stroke" />
          )}
          {/* Punctul însuși, ca să se vadă de unde pleacă amândouă. */}
          <circle cx={evacAleasa.d.point[0]} cy={evacAleasa.d.point[1]}
                  r={1.1} stroke={EVAC_LUNG} strokeWidth={2}
                  vectorEffect="non-scaling-stroke" />
          <circle cx={evacAleasa.d.point[0]} cy={evacAleasa.d.point[1]}
                  r={0.25} fill={EVAC_LUNG} stroke="none" />
        </g>
      )}
    </g>
    </ConventieCtx.Provider>
  );
}

/** Sfertul de cerc al canatului, din balamaua `(hx, hy)`, rază `r`: de la
 *  celălalt toc (+x) până la capătul canatului (+y), în cadrul local al
 *  golului. */
function arc(hx: number, hy: number, r: number, oglindit = false): string {
  // `oglindit`: balamaua e în tocul din DREAPTA, deci foaia închisă stă spre −x
  // și arcul merge de la 180° la 90° — foaia a doua a unei uși duble.
  const p: string[] = [];
  for (let a = 0; a <= 90; a += 7.5) {
    const t = ((oglindit ? 180 - a : a) * Math.PI) / 180;
    p.push(`${(hx + r * Math.cos(t)).toFixed(4)},${(hy + r * Math.sin(t)).toFixed(4)}`);
  }
  return p.join(" ");
}

/** Un gol în perete. Fâșia se umple cu hârtie (peretele e tăiat acolo), apoi:
 *  fereastra primește linia de tâmplărie pe mijloc, ușa un canat cu arc de
 *  deschidere. Fără arc, o ușă și o fereastră arată identic pe plan. */
/** Scara de pisică văzută de sus: conturul ei și treptele, linii în lungul
 *  lor (`deg`), câte una la fiecare sfert din cât iese din perete. */
function ScaraDePisica({ l, pick, ales }: { l: StoreyShape; pick: PickAttrs; ales: boolean }) {
  const pal = usePaleta();
  const cx = l.poly.reduce((s, p) => s + p[0], 0) / l.poly.length;
  const cy = l.poly.reduce((s, p) => s + p[1], 0) / l.poly.length;
  const w = l.width_m ?? 0.4, h = l.height_m ?? 0.2;
  const acc = "var(--acc, #0b7f92)";
  return (
    <g {...pick}>
      <polygon points={puncte(l.poly)} fill={pal.hartie}
               stroke={ales ? acc : OTEL} strokeWidth={ales ? 2 : 0.8}
               vectorEffect="non-scaling-stroke" />
      <g transform={`translate(${cx} ${cy}) rotate(${l.deg ?? 0})`} pointerEvents="none">
        {[-h / 4, 0, h / 4].map((y) => (
          <line key={y} x1={-w / 2} y1={y} x2={w / 2} y2={y} stroke={OTEL}
                strokeWidth={0.6} vectorEffect="non-scaling-stroke" />
        ))}
      </g>
    </g>
  );
}

/** Golurile care poartă o etichetă pe stratul obiectelor («Etichete»): tâmplăria
 *  adăposturilor ALA (indicativ UME/OME) și ușile căilor de evacuare, cu
 *  rezistența lor la foc («EI 60-C», scrisă pe obiect de `evacuare/pas.py`),
 *  la fel ca pe ALA (Oana, 19.10.2026). */
function eticheteazaGolul(o: StoreyShape & { tip: "Door" | "Window" }): boolean {
  return /^(UME|OME)/.test(o.ala?.indicativ ?? "") || (o.tip === "Door" && !!o.evacuare && !!o.rezistenta);
}

/** Textul etichetei golului: «UME1 · prag 15 cm» pe ALA. Clasa ușii de evacuare
 *  («EI 60-C») nu se mai scrie ca text: o spune emblema de pe gol
 *  (`EmblemaGol`), ca pe planșa de referință (Oana, 20.10.2026). */
function textulGolului(o: StoreyShape & { tip: "Door" | "Window" }, prag: string): string {
  if (o.ala?.indicativ) {
    return o.ala.indicativ + (o.ala.prag_m ? ` · ${prag} ${Math.round(o.ala.prag_m * 100)} cm` : "");
  }
  return "";
}

/** Emblema golului: cerc centrat pe gol, cam cât lățimea ușii (ø 0,84 m la o
 *  ușă de 0,90), cu hârtie dedesubt ca să se citească peste foaie și arc, și
 *  textul în culoarea clasei. Nu se rotește cu ușa: se citește ca orice text al
 *  planșei. */
function EmblemaGol({ o, emblema }: {
  o: StoreyShape & { tip: "Door" | "Window" }; emblema: Emblema;
}) {
  const pal = usePaleta();
  const cx = o.cx, cy = o.cy;
  if (cx == null || cy == null) return null;
  const R = 0.42;
  const doua = emblema.randuri.length > 1;
  return (
    <g pointerEvents="none" data-emblema={emblema.fel}>
      <circle cx={cx} cy={cy} r={R} fill={pal.hartie} fillOpacity={0.85}
              stroke={emblema.culoare} strokeWidth={1.4} vectorEffect="non-scaling-stroke" />
      {emblema.randuri.map((r, k) => (
        <text key={k} x={cx} y={cy + (doua ? (k === 0 ? -0.15 : 0.17) : 0)}
              fontSize={doua ? 0.26 : 0.3} fontWeight={700} fill={emblema.culoare}
              textAnchor="middle" dominantBaseline="central">{r}</text>
      ))}
    </g>
  );
}

/** Eticheta unui gol («UME1 · prag 15 cm», «EI 60-C»), scrisă la 1,4 m de gol,
 *  în partea în care se deschide foaia (`+y` local, v. `ala.pas._spre`, aceeași
 *  convenție la ușile de evacuare), puțin în lateral, cu o linie de indicație și
 *  un vârf de săgeată pe gol. */
function EticGol({ o, text }: { o: StoreyShape & { tip: "Door" | "Window" }; text: string }) {
  const pal = usePaleta();
  if (o.cx == null || o.cy == null || !text) return null;
  const r = ((o.deg ?? 0) * Math.PI) / 180;
  // în lungul golului și normala lui (+y local)
  const [ux, uy] = [Math.cos(r), Math.sin(r)];
  const [nx, ny] = [-Math.sin(r), Math.cos(r)];
  const [lx, ly] = [o.cx + nx * 1.4 + ux * 0.9, o.cy + ny * 1.4 + uy * 0.9];
  // vârful săgeții, pe gol, pe direcția liniei
  const [dx, dy] = [o.cx - lx, o.cy - ly];
  const L = Math.hypot(dx, dy) || 1;
  const [ax, ay] = [dx / L, dy / L];
  const varf = [[o.cx, o.cy],
                [o.cx - ax * 0.3 + ay * 0.1, o.cy - ay * 0.3 - ax * 0.1],
                [o.cx - ax * 0.3 - ay * 0.1, o.cy - ay * 0.3 + ax * 0.1]];
  const stanga = ux * (lx - o.cx) + uy * (ly - o.cy) < 0;
  return (
    <g pointerEvents="none">
      <line x1={lx} y1={ly} x2={o.cx} y2={o.cy} stroke={pal.nume}
            strokeWidth={0.8} vectorEffect="non-scaling-stroke" />
      <polygon points={puncte(varf)} fill={pal.nume} />
      <text x={lx} y={ly} fontSize={0.34} fill={pal.nume}
            textAnchor={stanga ? "end" : "start"} dominantBaseline="central"
            stroke={HARTIE} strokeWidth={0.08} paintOrder="stroke"
            strokeLinejoin="round">{text}</text>
    </g>
  );
}

/** UȘA DE INTRARE MUTATĂ, în previzualizare: golul ei în perete (hârtie, spre partea
 *  dinspre holul comun — cea fără camerele variantei) și linia ușii, în culoarea de accent. */
function IntrareMutata({ seg, camere, hartie, muchie }: {
  seg: number[][]; camere: { poly: number[][] }[]; hartie: string; muchie: string;
}) {
  const [a, b] = [seg[0], seg[seg.length - 1]];
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
  let nx = -(b[1] - a[1]) / L, ny = (b[0] - a[0]) / L;
  const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
  const inCamera = (x: number, y: number) =>
    camere.some((c) => c.poly.length >= 3 && inPoligon(x, y, c.poly));
  if (inCamera(mx + nx * 0.1, my + ny * 0.1)) { nx = -nx; ny = -ny; }
  const t = 0.32;
  const rect = [a, b, [b[0] + nx * t, b[1] + ny * t], [a[0] + nx * t, a[1] + ny * t]];
  return (
    <>
      <polygon points={puncte(rect)} fill={hartie} stroke={muchie} strokeWidth={0.6}
               vectorEffect="non-scaling-stroke" />
      <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke="var(--acc, #0b7f92)" strokeWidth={4}
            vectorEffect="non-scaling-stroke" />
    </>
  );
}

function inPoligon(x: number, y: number, poly: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Peretele cortină în plan: banda lui de la muchia plăcii până la fața
 *  capacelor, umplută cu hârtie, cu fața dinăuntru și cea de afară, linia
 *  sticlei și fiecare montant. CAPETELE (`ends_m`: colțul ieșind al corpului,
 *  stâlpul din fundul terasei) sunt profile de aluminiu de 16–24 cm: se
 *  desenează ca o cutie goală, cu contur de tuș, nu pline — pline, la
 *  mărimea asta, se citeau ca stâlpi de beton (Demo project, terasa retrasă a
 *  etajului 13, 28.10.2026). Montantul de 5 cm rămâne plin. */
function CortinaInPlan({ p, pick, ales }: { p: PanouFatada; pick: PickAttrs; ales: boolean }) {
  const pal = usePaleta();
  const c = p.cortina!;
  const n = p.n!;
  const L = Math.hypot(p.b[0] - p.a[0], p.b[1] - p.a[1]);
  if (L < 0.2) return null;
  const u = [(p.b[0] - p.a[0]) / L, (p.b[1] - p.a[1]) / L];
  const P = (x: number, y: number) => [p.a[0] + u[0] * x + n[0] * y, p.a[1] + u[1] * x + n[1] * y];
  const x0 = -c.capete[0], x1 = L + c.capete[1];
  const yIn = c.rost, yOut = p.depth_m;
  const yG = p.depth_m - c.capac - 0.012 - c.sticla.grosime / 2;
  const banda = [P(x0, 0), P(x1, 0), P(x1, yOut), P(x0, yOut)];
  const linie = (y: number) => { const a = P(x0, y), b = P(x1, y); return `${a[0]},${a[1]} ${b[0]},${b[1]}`; };
  const montanti = c.montanti.map((m) => Math.min(Math.max(m - c.latime / 2, 0), L - c.latime));
  // SELECTABILĂ ca orice tâmplărie: banda prinde clicul, liniile de peste ea nu
  return (
    <g {...pick}>
      <polygon points={puncte(banda)} fill={pal.hartie}
               stroke={ales ? "var(--acc, #0b7f92)" : "none"} strokeWidth={ales ? 2 : 0}
               vectorEffect="non-scaling-stroke" />
      <g pointerEvents="none">
      <polyline points={linie(yIn)} fill="none" stroke={pal.tus} strokeWidth={0.6} vectorEffect="non-scaling-stroke" />
      <polyline points={linie(yOut)} fill="none" stroke={pal.tus} strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
      <polyline points={linie(yG)} fill="none" stroke={pal.tus} strokeWidth={0.5} vectorEffect="non-scaling-stroke" />
      {montanti.map((x, i) => (
        <polygon key={i} points={puncte([P(x, yIn), P(x + c.latime, yIn), P(x + c.latime, yOut), P(x, yOut)])}
                 fill={pal.tus} stroke="none" />
      ))}
      {c.capete[0] > 0.01 && <polygon points={puncte([P(x0, 0), P(0, 0), P(0, yOut), P(x0, yOut)])}
                                      fill={pal.hartie} stroke={pal.tus} strokeWidth={0.9} vectorEffect="non-scaling-stroke" />}
      {c.capete[1] > 0.01 && <polygon points={puncte([P(L, 0), P(x1, 0), P(x1, yOut), P(L, yOut)])}
                                      fill={pal.hartie} stroke={pal.tus} strokeWidth={0.9} vectorEffect="non-scaling-stroke" />}
      </g>
    </g>
  );
}

function Gol({ o, pick, ales }: {
  o: StoreyShape & { tip: "Door" | "Window" };
  pick: PickAttrs; ales: boolean;
}) {
  // Convenția, din context — `Gol` e desenat de `StoreyView`, dar prin `map`,
  // deci nu-și primește paleta ca prop. V. `ConventieCtx`.
  const pal = usePaleta();
  const l = o.width_m ?? 0;
  const t = o.thickness_m ?? 0;
  const cx = o.cx, cy = o.cy;
  const acc = "var(--acc, #0b7f92)";
  return (
    <g {...pick}>
      {o.poly.length >= 3 && (
        <polygon points={puncte(o.poly)} fill={pal.hartie}
                 stroke={ales ? acc : pal.muchie} strokeWidth={ales ? 2 : 0.6}
                 vectorEffect="non-scaling-stroke" />
      )}
      {cx != null && cy != null && l > 0 && (
        <g transform={`translate(${cx} ${cy}) rotate(${o.deg ?? 0})`}
           pointerEvents="none">
          {o.tip === "Window" ? (
            // tâmplăria: linia continuă pe axa golului, între tocuri
            <line x1={-l / 2} y1={0} x2={l / 2} y2={0} stroke={pal.tus}
                  strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
          ) : (
            (o.foi ?? 1) >= 2 ? (
            <>
              {/* UȘA CU DOUĂ FOI: câte un canat de o jumătate de gol, din
                  fiecare toc, cu arcul lui — o foaie de 1,80 m cu un arc de
                  1,80 m în cameră e o ușă care nu se construiește. */}
              <line x1={-l / 2} y1={0} x2={-l / 2} y2={l / 2} stroke={pal.tus}
                    strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
              <line x1={l / 2} y1={0} x2={l / 2} y2={l / 2} stroke={pal.tus}
                    strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
              <polyline points={arc(-l / 2, 0, l / 2)} fill="none"
                        stroke={pal.arc} strokeWidth={0.7}
                        strokeDasharray="2 1.5" vectorEffect="non-scaling-stroke" />
              <polyline points={arc(l / 2, 0, l / 2, true)} fill="none"
                        stroke={pal.arc} strokeWidth={0.7}
                        strokeDasharray="2 1.5" vectorEffect="non-scaling-stroke" />
            </>
            ) : (() => {
              // BALAMAUA ÎN TOCUL EI: `end` o pune la capătul de sfârșit al
              // golului. Fără ea, o ușă întoarsă ca să baleieze în cameră
              // (v. `goluri_exterioare.orienteaza_usile`) și-ar fi mutat
              // balamaua în celălalt colț.
              const sfarsit = o.balama === "end";
              const hx = sfarsit ? l / 2 : -l / 2;
              // UȘA CU PARTE FIXĂ: foaia de la tocul ei, iar restul golului
              // e geam fix — linia de tâmplărie, ca la o fereastră — cu un
              // montant între ele.
              const f = o.foaie_m && o.foaie_m < l ? o.foaie_m : l;
              const mx = sfarsit ? l / 2 - f : -l / 2 + f;
              return (
                <>
                  {/* canatul, deschis la 90°, și arcul lui */}
                  <line x1={hx} y1={0} x2={hx} y2={f} stroke={pal.tus}
                        strokeWidth={0.9} vectorEffect="non-scaling-stroke" />
                  {/* arcul de deschidere, eșantionat în jurul balamalei: un
                      `A` de SVG are două centre posibile pentru aceeași
                      coardă, iar steagul care alege între ele se răstoarnă
                      odată cu sensul golului — arcul ieșea pe partea greșită
                      a ușii. */}
                  <polyline points={arc(hx, 0, f, sfarsit)} fill="none"
                            stroke={pal.arc} strokeWidth={0.7}
                            strokeDasharray="2 1.5" vectorEffect="non-scaling-stroke" />
                  {f < l && (
                    <>
                      <line x1={mx} y1={0} x2={sfarsit ? -l / 2 : l / 2} y2={0}
                            stroke={pal.tus} strokeWidth={0.9}
                            vectorEffect="non-scaling-stroke" />
                      <line x1={mx} y1={-t / 2} x2={mx} y2={t / 2}
                            stroke={pal.tus} strokeWidth={0.8}
                            vectorEffect="non-scaling-stroke" />
                    </>
                  )}
                </>
              );
            })()
          )}
          {t > 0 && (
            // tocurile: două liniuțe pe grosimea peretelui, la capete
            <>
              <line x1={-l / 2} y1={-t / 2} x2={-l / 2} y2={t / 2} stroke={pal.tus}
                    strokeWidth={0.8} vectorEffect="non-scaling-stroke" />
              <line x1={l / 2} y1={-t / 2} x2={l / 2} y2={t / 2} stroke={pal.tus}
                    strokeWidth={0.8} vectorEffect="non-scaling-stroke" />
            </>
          )}
        </g>
      )}
    </g>
  );
}

/** MEMOIZAT: pânza se re-randează la fiecare cadru de panoramare și zoom, iar
 *  proprietățile de aici nu se schimbă între cadre (scara vine așezată, v.
 *  `useScaraDetaliului`). Fără `memo` desenul se reconcilia de la capăt de
 *  60 de ori pe secundă. */
export default memo(StoreyView);
